import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams, useOutletContext, useSearchParams, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  Alert,
  InputAdornment,
  Tooltip,
  Card,
  CardContent,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import BarcodeScannerComponent from 'react-qr-barcode-scanner';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CalculateIcon from '@mui/icons-material/Calculate';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PrintIcon from '@mui/icons-material/Print';
import SaveIcon from '@mui/icons-material/Save';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SearchIcon from '@mui/icons-material/Search';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import StorefrontIcon from '@mui/icons-material/Storefront';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DescriptionIcon from '@mui/icons-material/Description';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import {
  useCreateSalesOrderMutation,
  useCreateHoldMutation,
  useGetSalesOrderQuery,
  useLazyGetCustomersQuery,
  useRecordSalesPaymentMutation,
  useUpdateSalesOrderStatusMutation,
  useHoldSalesOrderMutation,
  useUnholdSalesOrderMutation,
  useCreateCustomerMutation,
  useGetActiveRegisterSessionQuery,
  useOpenRegisterSessionMutation,
  useCloseRegisterSessionMutation,
  useLazyGetCustomerProductHistoryQuery,
} from '../../../store/api/salesApi';
import { useLazyGetProductsQuery, useGetWarrantyPeriodsQuery } from '../../../store/api/productsApi';
import { useGetPaymentAccountsQuery } from '../../../store/api/purchaseApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import { useGetLocationsQuery } from '../../../store/api/inventoryApi';
import { useGetUsersQuery } from '../../../store/api/userApi';
import { useGetQuotationQuery, useLazyGetQuotationsQuery } from '../../../store/api/quotationsApi';
import { CustomerFormContent } from './CustomerForm';
import ProductFormContent from '../../products/pages/ProductFormContent';
import { selectUser } from '../../../store/slices/authSlice';
import { useGetMyActiveTripQuery } from '../../../store/api/vanSalesApi';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import EmailIcon from '@mui/icons-material/Email';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import { formatItemDisplayName } from '../utils/formatItemDisplayName';
import { shareSalesOrderPdf, exportSalesOrderToPdf } from '../utils/exportSalesOrderPdf';
import { useGetEmailTemplatesQuery } from '../../../store/api/emailTemplateApi';
import { useSendInvoiceEmailMutation, useSendInvoiceWhatsappMutation } from '../../../store/api/salesApi';
import { usePermission } from '../../../hooks/usePermission';
import { getLocalDateString, formatDisplayDateTime } from '../../../shared/utils/dateUtils';

const DEFAULT_VAT_PERCENT = 5;

function SalesOrderForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isView = !!id && id !== 'new';

  const { hasPermission, isAdmin } = usePermission();
  const canWholesale = isAdmin || hasPermission('sales.orders.wholesale_sale');

  const currentUser = useSelector(selectUser);

  const outletContext = useOutletContext();
  const setNavigationBlocked = outletContext?.setNavigationBlocked;

  const [createSalesOrder] = useCreateSalesOrderMutation();
  const [createHold] = useCreateHoldMutation();
  const [updateSalesOrderStatus] = useUpdateSalesOrderStatusMutation();
  const [holdSalesOrder] = useHoldSalesOrderMutation();
  const [unholdSalesOrder] = useUnholdSalesOrderMutation();
  const [createCustomer] = useCreateCustomerMutation();

  const handleCreateCashCustomer = async () => {
    try {
      const created = await createCustomer({
        name: 'Cash Customer',
        phone: '0000000000',
        email: 'cash@example.com',
      }).unwrap();
      if (created?.id) {
        setCustomerSearchOptions(prev => [created, ...prev]);
        setFormData(prev => ({ ...prev, customerId: created.id }));
      }
    } catch (err) {
      console.error(err);
      alert(err?.data?.message || 'Failed to create Cash Customer');
    }
  };

  const [triggerCustomerSearch, { data: customerSearchData, isFetching: searchingCustomers }] = useLazyGetCustomersQuery();
  const [customerSearchOptions, setCustomerSearchOptions] = useState([]);
  const [triggerProductSearch, { data: productSearchData, isFetching: searchingProducts }] =
    useLazyGetProductsQuery();
  const { data: company } = useGetCurrentCompanyQuery();
  const { data: locations } = useGetLocationsQuery();
  const { data: usersData } = useGetUsersQuery();
  const { data: paymentAccounts } = useGetPaymentAccountsQuery();
  const { data: warrantyPeriodsData } = useGetWarrantyPeriodsQuery();
  const userList = Array.isArray(usersData) ? usersData : usersData?.users ?? [];
  const { data: existingOrder, isLoading: loadingOrder } = useGetSalesOrderQuery(id, {
    skip: !isView,
  });
  const { data: activeVanTrip } = useGetMyActiveTripQuery(undefined, { skip: isView });

  // Quotation Convert & Import Handling
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const quotationIdFromUrl = searchParams.get('quotationId') || location.state?.quotationId;
  const [activeQuotationId, setActiveQuotationId] = useState(quotationIdFromUrl || '');
  const [importQuotationModalOpen, setImportQuotationModalOpen] = useState(false);
  const [quotationSearchTerm, setQuotationSearchTerm] = useState('');

  const { data: quotationData, isLoading: loadingQuotation } = useGetQuotationQuery(activeQuotationId, {
    skip: !activeQuotationId || isView,
  });
  const [triggerFetchQuotations, { data: quotationsListData, isFetching: fetchingQuotationsList }] = useLazyGetQuotationsQuery();

  const vanMode = !isView && activeVanTrip && activeVanTrip.id;
  const vanLocationId = activeVanTrip?.van?.locationId || activeVanTrip?.van?.location?.id;
  const vanName = activeVanTrip?.van?.name;
  const vanPlate = activeVanTrip?.van?.plateNumber;
  const vanTripNumber = activeVanTrip?.tripNumber;

  const currency = company?.currency || 'USD';
  const vatIncluded = company?.salesVatIncluded !== false;
  const isTaxEnabled = company?.taxLabel !== 'None' && Number(company?.vatRate ?? 0) > 0;
  const defaultVat = company?.taxLabel === 'None' ? 0 : Number(company?.vatRate ?? 0);
  const itemNameFormat = company?.printItemNameFormat || 'name_sku';
  const dec = typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2;

  const formatAmount = (value) => {
    const amount = Number(value || 0);
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        minimumFractionDigits: dec,
        maximumFractionDigits: dec,
      }).format(amount);
    } catch {
      return `${currency} ${amount.toFixed(dec)}`;
    }
  };

  const previewInvoiceNumber = (() => {
    const prefix = String(company?.invoicePrefix || 'INV').trim() || 'INV';
    const nextNum = Number(company?.invoiceNextNumber || 1) || 1;
    const paddingRaw = Number(company?.invoiceNumberPadding);
    const padding = Number.isInteger(paddingRaw) ? Math.min(12, Math.max(0, paddingRaw)) : 6;
    const numberPart = String(Math.max(1, nextNum)).padStart(padding, '0');
    return `${prefix}-${numberPart}`;
  })();

  const [formData, setFormData] = useState(() => ({
    customerId: '',
    invoiceNumber: '',
    invoiceDate: getLocalDateString(),
    orderDate: getLocalDateString(),
    salesType: 'cash',
    creditDays: '',
    salesPersonId: currentUser?.id || '',
    locationId: '',
    notes: '',
    roundOff: 0,
    quotationId: quotationIdFromUrl || '',
  }));

  const creditDaysOptions = (company?.creditDaysOptions || '7,14,30')
    .split(',')
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => Number.isInteger(n) && n > 0);

  const [items, setItems] = useState([]);
  const [pricingMode, setPricingMode] = useState('retail'); // 'retail' | 'wholesale'

  const handlePricingModeChange = (newMode) => {
    if (newMode === 'wholesale' && !canWholesale) return;
    setPricingMode(newMode);
    if (items.length > 0) {
      setItems((prev) =>
        prev.map((item) => {
          let basePrice = 0;
          if (newMode === 'wholesale') {
            basePrice = (item.wholesalePrice != null && Number(item.wholesalePrice) > 0)
              ? Number(item.wholesalePrice)
              : Number(item.sellingPrice != null ? item.sellingPrice : item.unitPrice || 0);
          } else {
            basePrice = Number(item.sellingPrice != null ? item.sellingPrice : item.unitPrice || 0);
          }
          const itemVat = item.vatPercent != null ? (Number.isFinite(Number(item.vatPercent)) ? Number(item.vatPercent) : 0) : defaultVat;
          const calculatedUnitPrice = (vatIncluded && itemVat > 0)
            ? Number((basePrice / (1 + itemVat / 100)).toFixed(company?.currencyDecimals ?? 2))
            : basePrice;
          return {
            ...item,
            unitPrice: calculatedUnitPrice,
            inclusivePriceInput: undefined,
            pricingType: newMode,
          };
        })
      );
    }
  };

  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [printCheckOpen, setPrintCheckOpen] = useState(false);
  const [activeSerialIndex, setActiveSerialIndex] = useState(null);
  const [activeSerialScanIndex, setActiveSerialScanIndex] = useState(null);
  const activeRowIndexRef = useRef(null);
  const latestItemsRef = useRef(items);
  useEffect(() => { latestItemsRef.current = items; }, [items]);
  const latestCustomerRef = useRef(formData.customerId);
  useEffect(() => { latestCustomerRef.current = formData.customerId; }, [formData.customerId]);
  const [historyModal, setHistoryModal] = useState({ open: false, productId: null, customerId: null, productName: '' });
  const [triggerGetHistory, { data: historyData, isFetching: fetchingHistory }] = useLazyGetCustomerProductHistoryQuery();
  const [costModal, setCostModal] = useState({ open: false, selectedIndex: 0 });
  const [quickScanPrice, setQuickScanPrice] = useState('');
  const [lockQuickScanPrice, setLockQuickScanPrice] = useState(false);

  const productSearchInputRef = useRef(null);
  const customerSearchInputRef = useRef(null);
  const quickPriceInputRef = useRef(null);

  const focusQuickPrice = () => {
    if (quickPriceInputRef.current) {
      quickPriceInputRef.current.focus();
      try { quickPriceInputRef.current.select(); } catch (e) {}
      return;
    }
    const el = document.getElementById('quick-scan-price-input');
    if (el) {
      el.focus();
      try { el.select(); } catch (e) {}
    }
  };

  const focusProductSearch = () => {
    if (productSearchInputRef.current) {
      productSearchInputRef.current.focus();
      try { productSearchInputRef.current.select(); } catch (e) {}
      return;
    }
    const el = document.getElementById('product-search-input') || document.querySelector('input[placeholder*="barcode"]') || document.querySelector('input[placeholder*="Scan"]');
    if (el) {
      if (el.tagName === 'INPUT') {
        el.focus();
        try { el.select(); } catch (e) {}
      } else {
        const inner = el.querySelector('input');
        if (inner) {
          inner.focus();
          try { inner.select(); } catch (e) {}
        }
      }
    }
  };

  const focusCustomerSearch = () => {
    if (customerSearchInputRef.current) {
      customerSearchInputRef.current.focus();
      try { customerSearchInputRef.current.select(); } catch (e) {}
      return;
    }
    const el = document.getElementById('customer-search-input') || document.querySelector('input[id*="customer"]');
    if (el) {
      if (el.tagName === 'INPUT') {
        el.focus();
        try { el.select(); } catch (e) {}
      } else {
        const inner = el.querySelector('input');
        if (inner) {
          inner.focus();
          try { inner.select(); } catch (e) {}
        }
      }
    }
  };

  const getCostCode = (costPrice) => {
    if (!company?.costPriceCodeMapping || company.costPriceCodeMapping.length < 10) return null;
    if (costPrice == null) return null;
    const priceStr = String(Math.round(costPrice));
    let code = '';
    const mapping = company.costPriceCodeMapping.toUpperCase();
    for (let char of priceStr) {
      if (char >= '0' && char <= '9') {
        code += mapping[parseInt(char, 10)];
      }
    }
    return code;
  };
  const [scanOpen, setScanOpen] = useState(false);
  const [scanError, setScanError] = useState('');
  const [pendingBarcode, setPendingBarcode] = useState('');
  const [duplicateDialog, setDuplicateDialog] = useState({
    open: false,
    existingIndex: -1,
    product: null,
  });
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [imeiModal, setImeiModal] = useState({ open: false, itemIndex: -1, imeis: [], text: '' });
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);

  const [whatsappDialogOpen, setWhatsappDialogOpen] = useState(false);
  const [whatsappPhone, setWhatsappPhone] = useState('');
  const [whatsappLoading, setWhatsappLoading] = useState(false);

  const { data: emailTemplates } = useGetEmailTemplatesQuery();
  const [sendInvoiceEmail] = useSendInvoiceEmailMutation();
  const [sendInvoiceWhatsapp] = useSendInvoiceWhatsappMutation();

  useEffect(() => {
    if (existingOrder?.customer?.email) {
      setEmailRecipient(existingOrder.customer.email);
    }
    if (existingOrder?.customer?.phone) {
      setWhatsappPhone(existingOrder.customer.phone);
    }
  }, [existingOrder]);

  const handleSendEmail = async () => {
    if (!emailRecipient) {
      alert('Recipient email is required');
      return;
    }
    setEmailLoading(true);
    try {
      // ── Generate PDF (same as print template) in browser, in memory ──
      let pdfBase64 = undefined;
      if (existingOrder) {
        try {
          const arrayBuffer = await exportSalesOrderToPdf(existingOrder, company, {
            save: false,
            itemFormat: company?.printItemNameFormat || 'name_sku',
            filename: (existingOrder.invoiceNumber || existingOrder.orderNumber || id),
          });
          if (arrayBuffer) {
            // Convert ArrayBuffer → Base64 string
            const uint8 = new Uint8Array(arrayBuffer);
            let binary = '';
            for (let i = 0; i < uint8.length; i++) binary += String.fromCharCode(uint8[i]);
            pdfBase64 = btoa(binary);
          }
        } catch (pdfErr) {
          console.warn('PDF generation for email failed, backend will generate fallback:', pdfErr);
        }
      }

      await sendInvoiceEmail({
        id,
        email: emailRecipient,
        templateId: selectedEmailTemplate || undefined,
        pdfBase64,  // ← null/undefined if generation failed → backend uses pdfkit
      }).unwrap();
      alert('Email sent successfully with PDF attachment!');
      setEmailDialogOpen(false);
    } catch (err) {
      console.error(err);
      alert(err?.data?.message || 'Failed to send email');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleSendWhatsapp = async () => {
    if (!whatsappPhone) {
      alert('Recipient phone number is required');
      return;
    }
    setWhatsappLoading(true);
    try {
      let pdfBase64 = undefined;
      if (existingOrder) {
        try {
          const arrayBuffer = await exportSalesOrderToPdf(existingOrder, company, {
            save: false,
            itemFormat: company?.printItemNameFormat || 'name_sku',
            filename: (existingOrder.invoiceNumber || existingOrder.orderNumber || id),
          });
          if (arrayBuffer) {
            const uint8 = new Uint8Array(arrayBuffer);
            let binary = '';
            for (let i = 0; i < uint8.length; i++) binary += String.fromCharCode(uint8[i]);
            pdfBase64 = btoa(binary);
          }
        } catch (pdfErr) {
          console.warn('PDF generation for WhatsApp failed, backend will generate fallback:', pdfErr);
        }
      }

      const res = await sendInvoiceWhatsapp({
        id,
        phone: whatsappPhone,
        pdfBase64,
      }).unwrap();

      if (res.waLink) {
        // If API is not configured, open the wa.me link in a new tab
        window.open(res.waLink, '_blank');
        alert('WhatsApp API not configured. A sharing link has been opened instead.');
      } else {
        alert('WhatsApp message sent successfully!');
      }
      setWhatsappDialogOpen(false);
    } catch (err) {
      console.error(err);
      alert(err?.data?.message || 'Failed to send WhatsApp message');
    } finally {
      setWhatsappLoading(false);
    }
  };

  useEffect(() => {
    if (setNavigationBlocked && !isView) {
      setNavigationBlocked(items.length > 0);
    }
    if (isView) return;
    const handleBeforeUnload = (e) => {
      if (items.length > 0) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.history.pushState({ isForm: true }, '', window.location.href);
    const handlePopState = (e) => {
      if (items.length > 0) {
        window.history.pushState({ isForm: true }, '', window.location.href);
        setCancelDialogOpen(true);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
      if (setNavigationBlocked) setNavigationBlocked(false);
    };
  }, [items, isView, setNavigationBlocked]);
  const [payments, setPayments] = useState(
    !!id && id !== 'new'
      ? []
      : [
        {
          method: 'cash',
          paymentAccountId: '',
          referenceNo: '',
          amount: '',
        },
      ]
  );
  const [autoPaymentAmount, setAutoPaymentAmount] = useState(true);
  const [billDiscount, setBillDiscount] = useState(0);

  const resetForm = () => {
    setFormData({
      customerId: company?.defaultCustomerId || '',
      invoiceNumber: '',
      invoiceDate: getLocalDateString(),
      orderDate: getLocalDateString(),
      salesType: 'cash',
      creditDays: '',
      salesPersonId: currentUser?.id || '',
      locationId: vanMode && vanLocationId ? vanLocationId : '',
      notes: '',
      roundOff: 0,
    });
    setItems([]);
    setPayments([
      {
        method: 'cash',
        paymentAccountId: '',
        referenceNo: '',
        amount: '',
      },
    ]);
    setAutoPaymentAmount(true);
    setBillDiscount(0);
    setProductSearch('');
    setPendingBarcode('');
  };

  const formRef = useRef(null);
  const submitModeRef = useRef('save'); // 'save' | 'save-print' | 'quotation' - read in handleSubmit
  const [receivePaymentOpen, setReceivePaymentOpen] = useState(false);
  const [receivePaymentForm, setReceivePaymentForm] = useState({
    paymentDate: getLocalDateString(),
    amount: '',
    paymentMethod: 'cash',
    paymentAccountId: '',
    referenceNo: '',
    notes: '',
  });
  const [recordSalesPayment, { isLoading: recordingPayment }] = useRecordSalesPaymentMutation();

  const [registerOpen, setRegisterOpen] = useState(true);
  const [openRegisterDialogOpen, setOpenRegisterDialogOpen] = useState(false);
  const [closeRegisterDialogOpen, setCloseRegisterDialogOpen] = useState(false);
  const [closingPettyCashInput, setClosingPettyCashInput] = useState('');
  const [openingBalanceInput, setOpeningBalanceInput] = useState('');
  const [closingBalanceInput, setClosingBalanceInput] = useState('');
  const [registerSession, setRegisterSession] = useState(null);

  const { data: dbSession, refetch: refetchSession, isUninitialized: sessionUninitialized, isLoading: sessionLoading } = useGetActiveRegisterSessionQuery(undefined, { skip: isView || company?.enableSalesSession !== true });
  const [openRegisterSessionApi] = useOpenRegisterSessionMutation();
  const [closeRegisterSessionApi] = useCloseRegisterSessionMutation();

  useEffect(() => {
    if (isView) return;
    if (!company) return;
    
    if (company.enableSalesSession !== true) {
      setRegisterOpen(true);
      setOpenRegisterDialogOpen(false);
      return;
    }

    if (sessionLoading || sessionUninitialized) return;

    if (dbSession) {
      setRegisterSession(dbSession);
      setRegisterOpen(true);
      setOpenRegisterDialogOpen(false);
    } else {
      setRegisterSession(null);
      setRegisterOpen(false);
      setOpenRegisterDialogOpen(true);
    }
  }, [isView, company, dbSession, sessionLoading, sessionUninitialized]);

  const handleOpenRegister = async () => {
    try {
      const openingAmt = parseFloat(openingBalanceInput) || 0;
      await openRegisterSessionApi({ openingBalance: openingAmt }).unwrap();
      if (!sessionUninitialized) refetchSession();
    } catch (err) {
      alert(err?.data?.message || 'Failed to open register session');
    }
  };

  const handleCloseRegister = async () => {
    try {
      const actualCash = parseFloat(closingBalanceInput) || 0;
      const pettyCash = parseFloat(closingPettyCashInput) || 0;

      const fin = registerSession?.financials;
      const opening = Number(fin?.openingBalance ?? registerSession?.openingBalance ?? 0);
      const cashSales = Number(fin?.cashSales ?? 0);
      const cardSales = Number(fin?.cardSales ?? 0);
      const creditSales = Number(fin?.creditSales ?? 0);
      const bankSales = Number(fin?.bankSales ?? 0);
      const chequeSales = Number(fin?.chequeSales ?? 0);
      const creditReceivedCash = Number(fin?.customerCreditCollectedCash ?? 0);
      const creditReceivedCard = Number(fin?.customerCreditCollectedCard ?? 0);
      const creditReceivedBank = Number(fin?.customerCreditCollectedBank ?? 0);
      const cashPurchases = Number(fin?.cashPurchases ?? 0);
      const cashExpenses = Number(fin?.cashExpenses ?? 0);
      const supplierPaymentsCash = Number(fin?.supplierPaymentsCash ?? 0);
      const cashRefunds = Number(fin?.cashRefunds ?? 0);
      const tradeInPayoutCash = Number(fin?.tradeInCashPayout ?? 0);
      const cashToBank = Number(fin?.cashToBankTransfers ?? 0);
      const bankToCash = Number(fin?.bankToCashTransfers ?? 0);

      const totalCashIn = Number(fin?.totalCashIn ?? (opening + cashSales + creditReceivedCash + bankToCash));
      const totalCashOut = Number(fin?.totalCashOut ?? (cashPurchases + cashExpenses + supplierPaymentsCash + cashRefunds + tradeInPayoutCash + cashToBank));
      const expected = Number(fin?.expectedClosingCash ?? (totalCashIn - totalCashOut));

      const transferAmount = Math.max(0, actualCash - pettyCash);
      const discrepancy = actualCash - expected;

      let reportMsg = `========================================\n`;
      reportMsg += `     SHIFT CLOSING SUMMARY REPORT       \n`;
      reportMsg += `========================================\n`;
      reportMsg += `Shift Opened: ${registerSession?.openedAt ? formatDisplayDateTime(registerSession.openedAt, company?.dateFormat, company?.timezone) : '–'}\n`;
      reportMsg += `Shift Closed: ${formatDisplayDateTime(new Date(), company?.dateFormat, company?.timezone)}\n\n`;

      reportMsg += `--- SALES SUMMARY ---\n`;
      reportMsg += `Total Orders: ${fin?.salesCount ?? registerSession?.salesCount ?? 0}\n`;
      reportMsg += `Total Sales: ${Number(fin?.totalSales ?? registerSession?.salesTotal ?? 0).toFixed(2)} ${currency}\n`;
      reportMsg += `  • Cash Sales (In Drawer): ${cashSales.toFixed(2)} ${currency}\n`;
      reportMsg += `  • Card / POS Sales (Bank): ${cardSales.toFixed(2)} ${currency}\n`;
      if (bankSales > 0) reportMsg += `  • Bank Transfer Sales: ${bankSales.toFixed(2)} ${currency}\n`;
      if (chequeSales > 0) reportMsg += `  • Cheque Sales: ${chequeSales.toFixed(2)} ${currency}\n`;
      if (creditSales > 0) reportMsg += `  • Credit Sales (Receivable): ${creditSales.toFixed(2)} ${currency}\n`;

      if (creditReceivedCash > 0 || creditReceivedCard > 0 || creditReceivedBank > 0 || bankToCash > 0) {
        reportMsg += `\n--- CASH INFLOWS / DEBT COLLECTIONS ---\n`;
        if (creditReceivedCash > 0) reportMsg += `  • Credit Received (Cash): +${creditReceivedCash.toFixed(2)} ${currency}\n`;
        if (bankToCash > 0) reportMsg += `  • Bank to Cash (Withdrawal / Cash In): +${bankToCash.toFixed(2)} ${currency}\n`;
        if (creditReceivedCard > 0) reportMsg += `  • Credit Received (Card): +${creditReceivedCard.toFixed(2)} ${currency}\n`;
        if (creditReceivedBank > 0) reportMsg += `  • Credit Received (Bank): +${creditReceivedBank.toFixed(2)} ${currency}\n`;
      }

      if (cashPurchases > 0 || cashExpenses > 0 || supplierPaymentsCash > 0 || cashRefunds > 0 || tradeInPayoutCash > 0 || cashToBank > 0) {
        reportMsg += `\n--- DRAWER CASH OUTFLOWS ---\n`;
        if (cashToBank > 0) reportMsg += `  • Cash to Bank (Deposit / Drop): -${cashToBank.toFixed(2)} ${currency}\n`;
        if (cashPurchases > 0) reportMsg += `  • Cash Purchases: -${cashPurchases.toFixed(2)} ${currency}\n`;
        if (cashExpenses > 0) reportMsg += `  • Cash Expenses: -${cashExpenses.toFixed(2)} ${currency}\n`;
        if (supplierPaymentsCash > 0) reportMsg += `  • Supplier Credit Paid (Cash): -${supplierPaymentsCash.toFixed(2)} ${currency}\n`;
        if (cashRefunds > 0) reportMsg += `  • Cash Refunds: -${cashRefunds.toFixed(2)} ${currency}\n`;
        if (tradeInPayoutCash > 0) reportMsg += `  • Trade-In Payout (Cash): -${tradeInPayoutCash.toFixed(2)} ${currency}\n`;
      }

      reportMsg += `\n========================================\n`;
      reportMsg += `--- CASH DRAWER RECONCILIATION ---\n`;
      reportMsg += `Opening Cash Float: ${opening.toFixed(2)} ${currency}\n`;
      reportMsg += `(+) Total Cash In: +${(cashSales + creditReceivedCash + bankToCash).toFixed(2)} ${currency}\n`;
      reportMsg += `(-) Total Cash Out: -${totalCashOut.toFixed(2)} ${currency}\n`;
      reportMsg += `----------------------------------------\n`;
      reportMsg += `Expected Cash in Drawer: ${expected.toFixed(2)} ${currency}\n`;
      reportMsg += `Actual Cash Counted: ${actualCash.toFixed(2)} ${currency}\n`;
      reportMsg += `Discrepancy: ${discrepancy >= 0 ? '+' : ''}${discrepancy.toFixed(2)} ${currency} (${Math.abs(discrepancy) < 0.01 ? 'Balanced' : (discrepancy > 0 ? 'Cash Surplus' : 'Cash Shortage')})\n`;
      reportMsg += `----------------------------------------\n`;
      reportMsg += `Petty Cash Kept in Drawer: ${pettyCash.toFixed(2)} ${currency}\n`;
      reportMsg += `Net Cash Transferred to Safe: ${transferAmount.toFixed(2)} ${currency}\n`;

      if (cardSales > 0 || creditReceivedCard > 0) {
        const totalCard = Number(fin?.totalCardSettlement ?? (cardSales + creditReceivedCard));
        reportMsg += `\nNon-Cash POS Settlement (Card): ${totalCard.toFixed(2)} ${currency} (Settled directly to Bank)\n`;
      }
      reportMsg += `========================================`;

      await closeRegisterSessionApi({ closingBalance: actualCash, closingPettyCash: pettyCash }).unwrap();
      setRegisterSession(null);
      setRegisterOpen(false);
      setCloseRegisterDialogOpen(false);
      setOpeningBalanceInput('');
      setClosingBalanceInput('');
      setClosingPettyCashInput('');
      setOpenRegisterDialogOpen(true);
      alert(reportMsg);
      if (!sessionUninitialized) refetchSession();
    } catch (err) {
      alert(err?.data?.message || 'Failed to close register session');
    }
  };

  useEffect(() => {
    if (customerSearchData) {
      const data = customerSearchData.data || (Array.isArray(customerSearchData) ? customerSearchData : []);
      setCustomerSearchOptions(prevOptions => {
        // Find if current selection is missing from new data
        const currentId = formData.customerId;
        const currentInNew = data.find(c => c.id === currentId);
        const currentInOld = prevOptions.find(c => c.id === currentId);

        if (currentId && !currentInNew && currentInOld) {
          return [currentInOld, ...data];
        }
        return data;
      });
    }
  }, [customerSearchData, formData.customerId]);

  useEffect(() => {
    if (existingOrder?.customer && !customerSearchOptions.find(c => c.id === existingOrder.customer.id)) {
      setCustomerSearchOptions(prev => [existingOrder.customer, ...prev]);
    }
  }, [existingOrder, customerSearchOptions]);
  useEffect(() => {
    if (!isView && company?.defaultCustomerId) {
      if (!formData.customerId) {
        setFormData(prev => ({ ...prev, customerId: company.defaultCustomerId }));
      }
      triggerCustomerSearch({ limit: 100 });
    }
  }, [company, isView, triggerCustomerSearch]);

  const selectedCustomer = customerSearchOptions.find((c) => c.id === formData.customerId) || null;

  useEffect(() => {
    if (!scanOpen) {
      setScanError('');
      setPendingBarcode('');
    }
  }, [scanOpen]);

  const handleAddProductRow = (product, forceNew = false) => {
    if (!product) return;
    const behavior = company?.barcodeAfterScanBehaviorSales || company?.barcodeAfterScanBehavior || 'search';
    const dupBehavior =
      company?.duplicateItemBehaviorSales ||
      (behavior === 'auto_increment' ? 'auto_increment' : behavior === 'add_new' ? 'add_new' : 'dialog');

    // Check if quick scan custom price is active
    const hasCustomPrice = quickScanPrice !== '' && !isNaN(Number(quickScanPrice)) && Number(quickScanPrice) >= 0;
    const customPriceNum = hasCustomPrice ? Number(quickScanPrice) : null;

    // Calculate rawBasePrice and computedUnitPrice (respecting VAT inclusive / exclusive configuration)
    const rawBasePrice =
      customPriceNum !== null
        ? customPriceNum
        : (pricingMode === 'wholesale' && product.wholesalePrice != null && Number(product.wholesalePrice) > 0
          ? Number(product.wholesalePrice)
          : (product.sellingPrice ?? 0));
    const computedUnitPrice =
      vatIncluded && defaultVat > 0
        ? Number((rawBasePrice / (1 + defaultVat / 100)).toFixed(company?.currencyDecimals ?? 2))
        : rawBasePrice;

    setItems((prev) => {
      const existingIndex = prev.findIndex((it) => String(it.productId) === String(product.id));
      if (existingIndex !== -1 && !forceNew) {
        if (dupBehavior === 'auto_increment') {
          const updated = [...prev];
          const curQty = Number(updated[existingIndex].quantity || 0) || 0;
          updated[existingIndex] = {
            ...updated[existingIndex],
            quantity: String(Math.max(0.01, curQty + 1)),
            ...(hasCustomPrice && {
              unitPrice: computedUnitPrice,
              sellingPrice: customPriceNum,
              pricingType: 'custom',
            }),
          };
          setTimeout(focusProductSearch, 100);
          return updated;
        }

        if (dupBehavior !== 'add_new') {
          setDuplicateDialog({ open: true, existingIndex, product, customPrice: customPriceNum });
          if (behavior === 'quantity') {
            setTimeout(() => {
              const el = document.getElementById(`qty-input-${existingIndex}`);
              if (el) {
                el.focus();
                el.select();
              }
            }, 100);
          } else if (behavior === 'price') {
            setTimeout(() => {
              const el = document.getElementById(`price-input-${existingIndex}`);
              if (el) {
                el.focus();
                el.select();
              }
            }, 100);
          } else {
            setTimeout(focusProductSearch, 100);
          }
          return prev;
        }
      }

      if (behavior === 'quantity') {
        setTimeout(() => {
          const el = document.getElementById('qty-input-0');
          if (el) {
            el.focus();
            el.select();
          }
        }, 100);
      } else if (behavior === 'price') {
        setTimeout(() => {
          const el = document.getElementById('price-input-0');
          if (el) {
            el.focus();
            el.select();
          }
        }, 100);
      } else {
        setTimeout(focusProductSearch, 100);
      }

      return [
        {
          productId: product.id,
          productName: product.name,
          productSku: product.sku,
          productBrand: product.brand,
          productBarcode: product.barcode,
          sellingPrice: customPriceNum !== null ? customPriceNum : (product.sellingPrice ?? 0),
          wholesalePrice: product.wholesalePrice ?? 0,
          pricingType:
            customPriceNum !== null
              ? 'custom'
              : (pricingMode === 'wholesale' && product.wholesalePrice != null && Number(product.wholesalePrice) > 0
                ? 'wholesale'
                : 'retail'),
          maxDiscountType:
            product.maxDiscountType === 'percent' || product.maxDiscountType === 'amount'
              ? product.maxDiscountType
              : undefined,
          maxDiscountPercent: product.maxDiscountPercent != null ? product.maxDiscountPercent : undefined,
          maxDiscountAmount: product.maxDiscountAmount != null ? product.maxDiscountAmount : undefined,
          requiresImei: !!product.requiresImei,
          requiresDualImei: !!product.requiresDualImei,
          imeis: [],
          quantity: 1,
          unit: 'pcs',
          unitPrice: computedUnitPrice,
          discount: 0,
          vatPercent: defaultVat,
          costPrice: product.costPrice ?? 0,
          isReturn: false,
          isDamaged: false,
          damageReason: '',
          productType: product.type || 'product',
          hasWarranty: !!product.hasWarranty,
          warrantyPeriodId: product.warrantyPeriodId || null,
          serialNumbers: [],
          serviceData: product.type === 'service' ? { imei: '', deviceModel: '', complains: '' } : null,
        },
        ...prev,
      ];
    });

    if (hasCustomPrice && !lockQuickScanPrice) {
      setQuickScanPrice('');
    }
  };

  useEffect(() => {
    const handleHelp = (e) => {
      e.preventDefault();
      return false;
    };
    window.onhelp = handleHelp;

    const handleKeyDown = (e) => {
      // Keyboard shortcuts for POS / Sales Order Form
      if (e.key === 'F1') {
        e.preventDefault();
        e.stopPropagation();
        console.log('F1 pressed: Focusing Product Search');
        focusProductSearch();
      } else if (e.key === 'F2' || e.key === 'F9') {
        e.preventDefault();
        e.stopPropagation();
        console.log('F2/F9 pressed: Saving/Creating Order');
        submitModeRef.current = 'save';
        formRef.current?.requestSubmit();
      } else if (e.key === 'F3' || e.key === 'F8') {
        e.preventDefault();
        e.stopPropagation();
        console.log('F3/F8 pressed: Holding Order');
        submitModeRef.current = 'save-hold';
        formRef.current?.requestSubmit();
      } else if (e.key === 'F4' || e.key === 'F10') {
        e.preventDefault();
        e.stopPropagation();
        console.log('F4/F10 pressed: Saving & Printing Order');
        submitModeRef.current = 'save-print';
        formRef.current?.requestSubmit();
      } else if (e.key === 'F5') {
        e.preventDefault();
        e.stopPropagation();
        console.log('F5 pressed: Focusing Customer Search');
        focusCustomerSearch();
      } else if (e.key === 'F6') {
        e.preventDefault();
        e.stopPropagation();
        console.log('F6 pressed: Cancel order dialog');
        setCancelDialogOpen(true);
      } else if (e.key === 'F7') {
        e.preventDefault();
        e.stopPropagation();
        console.log('F7 pressed: Opening Item Cost Modal');
        const activeIdx = activeRowIndexRef.current ?? 0;
        setCostModal({ open: true, selectedIndex: activeIdx });
      } else if (e.key === 'F11' || (e.altKey && e.key.toLowerCase() === 'h')) {
        e.preventDefault();
        e.stopPropagation();
        console.log('F11/Alt+H pressed: Opening History Modal');
        const activeIdx = activeRowIndexRef.current;
        const currentItems = latestItemsRef.current;
        const custId = latestCustomerRef.current;
        if (activeIdx !== null && currentItems[activeIdx]) {
          const prodId = currentItems[activeIdx].productId;
          if (!custId) {
             alert('Please select a customer first to view price history.');
             return;
          }
          if (!prodId) {
             alert('This row does not have a valid product selected.');
             return;
          }
          setHistoryModal({ open: true, productId: prodId, customerId: custId, productName: currentItems[activeIdx].productName });
          triggerGetHistory({ customerId: custId, productId: prodId });
        } else {
          alert('Please click on a line item row to select it first.');
        }
      } else if (e.altKey && e.key.toLowerCase() === 'p') {
        e.preventDefault();
        e.stopPropagation();
        focusQuickPrice();
      } else if (e.key === 'Enter') {
        const active = document.activeElement;
        if (!active) return;
        
        // Only target normal input fields (not textareas, not combo boxes)
        if (active.tagName !== 'INPUT' && active.tagName !== 'SELECT') return;
        if (active.getAttribute('role') === 'combobox') return;
        if (active.closest('.MuiAutocomplete-root')) return;
        if (active.type === 'button' || active.type === 'submit' || active.type === 'reset') return;

        e.preventDefault(); // Prevent accidental form submission
        
        // Find all focusable elements
        const focusable = Array.from(document.querySelectorAll('input:not([disabled]), select:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])'))
          .filter(el => !el.disabled && el.offsetParent !== null); // check if visible
          
        const index = focusable.indexOf(active);
        if (index > -1 && index + 1 < focusable.length) {
          focusable[index + 1].focus();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      window.onhelp = null;
    };
  }, []);

  useEffect(() => {
    if (!pendingBarcode) return;
    const list = productSearchData?.products || [];
    if (!Array.isArray(list) || list.length === 0) return;

    const code = String(pendingBarcode).trim();
    if (!code) return;

    const match = list.find((p) => String(p?.barcode || '').trim() === code);
    if (match) {
      handleAddProductRow(match);
      setProductSearch('');
      setPendingBarcode('');
      setScanOpen(false);
    }
  }, [pendingBarcode, productSearchData, handleAddProductRow]);

  useEffect(() => {
    const val = String(productSearch).trim();
    if (!val || val.length < 3) return;
    if (searchingProducts) return;
    const list = productSearchData?.products || [];
    if (list.length === 0) return;

    const match = list.find((p) =>
      String(p?.barcode || '').trim() === val ||
      String(p?.sku || '').trim() === val
    );

    if (match) {
      handleAddProductRow(match);
      setProductSearch('');
    }
  }, [productSearchData, productSearch, searchingProducts]);

  const handleRemoveItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const toNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

  const computeLine = (item) => {
    const isRet = !!item.isReturn;
    const qty = toNum(item.quantity);
    const price = toNum(item.unitPrice);
    const discount = toNum(item.discount);
    const vatPct = toNum(item.vatPercent);
    const lineSubtotal = Math.max(0, qty * price - discount);
    let vatAmount = 0;
    if (vatPct > 0) {
      vatAmount = lineSubtotal * (vatPct / 100);
    }
    const lineTotal = lineSubtotal + vatAmount;

    const dec = typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2;
    const multiplier = isRet ? -1 : 1;

    return {
      vatAmount: Math.round((vatAmount) * Math.pow(10, dec)) / Math.pow(10, dec),
      lineTotal: Math.round((lineTotal) * Math.pow(10, dec)) / Math.pow(10, dec),
      lineTotalMixed: Math.round((lineTotal * multiplier) * Math.pow(10, dec)) / Math.pow(10, dec),
      lineSubtotal,
    };
  };

  const orderSubtotal = items.reduce(
    (sum, item) => sum + (item.isReturn ? -1 : 1) * toNum(item.quantity) * toNum(item.unitPrice),
    0
  );
  const lineDiscountTotal = items.reduce((sum, item) => sum + (item.isReturn ? -1 : 1) * toNum(item.discount), 0);
  const orderDiscountTotal = lineDiscountTotal + toNum(billDiscount);
  const orderVatTotal = items.reduce((sum, item) => sum + (item.isReturn ? -1 : 1) * computeLine(item).vatAmount, 0);
  const netOrder = orderSubtotal - orderDiscountTotal;
  const grandTotal = netOrder + orderVatTotal;
  const roundOff = toNum(formData.roundOff);
  const totalAmount = grandTotal + roundOff;

  const paidTotal = payments.reduce(
    (sum, p) => sum + (Number.isFinite(Number(p.amount)) ? Number(p.amount) : 0),
    0
  );
  const remaining = totalAmount - paidTotal;
  const change = paidTotal - totalAmount;
  const totalQty = items.reduce((sum, item) => sum + toNum(item.quantity), 0);

  const handleQuickMethod = (method) => {
    setAutoPaymentAmount(false);
    setPayments([
      {
        method,
        paymentAccountId: '',
        referenceNo: '',
        amount: totalAmount > 0 ? totalAmount.toFixed(dec) : '0.00',
      },
    ]);
  };

  const handleQuickCashTender = (tenderAmount) => {
    setAutoPaymentAmount(false);
    setPayments([
      {
        method: 'cash',
        paymentAccountId: '',
        referenceNo: '',
        amount: Number(tenderAmount).toFixed(dec),
      },
    ]);
  };

  // If creating a new order and there is a logged-in user, default them as sales person
  useEffect(() => {
    if (!isView && currentUser?.id && !formData.salesPersonId) {
      setFormData((prev) => ({ ...prev, salesPersonId: currentUser.id }));
    }
  }, [isView, currentUser, formData.salesPersonId]);

  // Van mode: auto-set locationId and salesType to cash when user has active van trip
  useEffect(() => {
    if (vanMode && vanLocationId && !isView) {
      setFormData((prev) => ({
        ...prev,
        locationId: vanLocationId,
        salesType: 'cash',
      }));
    }
  }, [vanMode, vanLocationId, isView]);

  useEffect(() => {
    if (!isView && !vanMode && locations?.length && !formData.locationId) {
      const mainWarehouse = locations.find((l) => l.name.toLowerCase().includes('main warehouse'));
      if (mainWarehouse) {
        setFormData((prev) => ({ ...prev, locationId: mainWarehouse.id }));
      } else if (locations.length > 0) {
        setFormData((prev) => ({ ...prev, locationId: locations[0].id }));
      }
    }
  }, [locations, vanMode, isView]);

  useEffect(() => {
    if (existingOrder && isView) {
      setFormData({
        customerId: existingOrder.customerId || '',
        invoiceNumber: existingOrder.invoiceNumber || '',
        invoiceDate: existingOrder.invoiceDate
          ? existingOrder.invoiceDate.substring(0, 10)
          : formData.invoiceDate,
        orderDate: existingOrder.orderDate
          ? existingOrder.orderDate.substring(0, 10)
          : formData.orderDate,
        salesType: existingOrder.salesType || 'cash',
        creditDays: existingOrder.creditDays != null ? String(existingOrder.creditDays) : '',
        salesPersonId: existingOrder.salesPersonId || '',
        locationId: existingOrder.locationId || '',
        notes: existingOrder.notes || '',
        roundOff: existingOrder.roundOff || 0,
      });
      const mappedItems = (existingOrder.items || []).map((it) => ({
        productId: it.productId,
        productName: it.product?.name,
        productSku: it.product?.sku,
        productBrand: it.product?.brand,
        productBarcode: it.product?.barcode,
        maxDiscountType: it.product?.maxDiscountType === 'percent' || it.product?.maxDiscountType === 'amount' ? it.product.maxDiscountType : undefined,
        maxDiscountPercent: it.product?.maxDiscountPercent != null ? it.product.maxDiscountPercent : undefined,
        maxDiscountAmount: it.product?.maxDiscountAmount != null ? it.product.maxDiscountAmount : undefined,
        quantity: it.quantity,
        unit: it.unit || 'pcs',
        unitPrice: (vatIncluded && (toNum(it.vatPercent) || defaultVat) > 0)
          ? Number((it.unitPrice / (1 + (toNum(it.vatPercent) || defaultVat) / 100)).toFixed(company?.currencyDecimals ?? 2))
          : it.unitPrice,
        discount: toNum(it.discount),
        vatPercent: toNum(it.vatPercent) || defaultVat,
        batchNo: it.batchNo || '',
        expiryDate: it.expiryDate ? it.expiryDate.substring(0, 10) : '',
        costPrice: it.product?.costPrice ?? 0,
        isReturn: !!it.isReturn,
        isDamaged: !!it.isDamaged,
        damageReason: it.damageReason || '',
        productType: it.product?.type || 'product',
        serviceData: it.serviceData ? (typeof it.serviceData === 'string' ? JSON.parse(it.serviceData) : it.serviceData) : null,
        hasWarranty: !!it.hasWarranty,
        warrantyPeriodId: it.warrantyPeriodId || null,
        requiresImei: !!it.product?.requiresImei,
        requiresDualImei: !!it.product?.requiresDualImei,
        imeis: (it.imeis || []).flatMap(imeiObj => {
          if (typeof imeiObj === 'string') return [imeiObj];
          const arr = [imeiObj.imei];
          if (imeiObj.secondaryImei) arr.push(imeiObj.secondaryImei);
          return arr;
        }),
        serialNumbers: typeof it.serialNumbers === 'string' ? JSON.parse(it.serialNumbers || '[]') : (it.serialNumbers || []),
      }));
      setItems(mappedItems);
      const lineDiscountTotal = mappedItems.reduce((sum, it) => sum + toNum(it.discount), 0);
      const orderDiscount = toNum(existingOrder.discountTotal);
      const extra = orderDiscount - lineDiscountTotal;
      setBillDiscount(extra > 0 ? extra : 0);
      if (existingOrder.payments && Array.isArray(existingOrder.payments) && existingOrder.payments.length > 0) {
        setPayments(existingOrder.payments.map((p) => ({
          method: p.method || 'cash',
          paymentAccountId: p.paymentAccountId || '',
          referenceNo: p.referenceNo || '',
          amount: p.amount || 0,
        })));
        setAutoPaymentAmount(false);
      } else {
        const paid = toNum(existingOrder.paidAmount);
        if (paid > 0) {
          setPayments([
            { method: 'cash', paymentAccountId: '', referenceNo: '', amount: paid },
          ]);
          setAutoPaymentAmount(false);
        }
      }
    }
  }, [existingOrder, isView]);

  // Synchronize quotation data when converting or importing a quotation
  useEffect(() => {
    if (quotationData && !isView) {
      if (quotationData.customer) {
        setCustomerSearchOptions((prev) => {
          if (!prev.find((c) => c.id === quotationData.customer.id)) {
            return [quotationData.customer, ...prev];
          }
          return prev;
        });
      }

      setFormData((prev) => ({
        ...prev,
        customerId: quotationData.customerId || prev.customerId,
        salesPersonId: quotationData.salesPersonId || prev.salesPersonId || currentUser?.id || '',
        locationId: quotationData.locationId || prev.locationId || '',
        notes: quotationData.notes
          ? `${quotationData.notes} (Ref Quotation: ${quotationData.quotationNumber})`
          : `Ref Quotation: ${quotationData.quotationNumber}`,
        salesType: (Number(quotationData.customer?.creditDays || 0) > 0 || Number(quotationData.customer?.creditLimit || 0) > 0) ? 'credit' : 'cash',
        creditDays: quotationData.customer?.creditDays ? String(quotationData.customer.creditDays) : prev.creditDays,
        quotationId: quotationData.id,
      }));

      if (Array.isArray(quotationData.items) && quotationData.items.length > 0) {
        const mappedItems = quotationData.items.map((it) => {
          const product = it.product || {};
          const vatPercentVal = it.vatPercent != null ? toNum(it.vatPercent) : defaultVat;
          const rawPrice = toNum(it.unitPrice || product.sellingPrice || product.retailPrice || 0);
          const computedUnitPrice = (vatIncluded && vatPercentVal > 0)
            ? Number((rawPrice / (1 + vatPercentVal / 100)).toFixed(company?.currencyDecimals ?? 2))
            : rawPrice;

          return {
            productId: it.productId,
            productName: product.name || it.productName || 'Unknown Product',
            productSku: product.sku || it.productSku || '',
            productBrand: product.brand || it.productBrand || '',
            productBarcode: product.barcode || it.productBarcode || '',
            sellingPrice: product.sellingPrice ?? rawPrice,
            wholesalePrice: product.wholesalePrice ?? 0,
            pricingType: pricingMode === 'wholesale' ? 'wholesale' : 'retail',
            maxDiscountType: product.maxDiscountType,
            maxDiscountPercent: product.maxDiscountPercent,
            maxDiscountAmount: product.maxDiscountAmount,
            quantity: toNum(it.quantity) || 1,
            unit: it.unit || product.unit || 'pcs',
            unitPrice: computedUnitPrice,
            discount: toNum(it.discount),
            vatPercent: vatPercentVal,
            costPrice: toNum(product.costPrice || 0),
            batchNo: it.batchNo || '',
            expiryDate: it.expiryDate ? it.expiryDate.substring(0, 10) : '',
            isReturn: false,
            isDamaged: false,
            damageReason: '',
            productType: product.type || 'product',
            serviceData: it.serviceData ? (typeof it.serviceData === 'string' ? JSON.parse(it.serviceData) : it.serviceData) : null,
            hasWarranty: !!product.hasWarranty,
            warrantyPeriodId: product.warrantyPeriodId || null,
            requiresImei: !!product.requiresImei,
            requiresDualImei: !!product.requiresDualImei,
            imeis: [],
            serialNumbers: [],
          };
        });
        setItems(mappedItems);

        const lineDiscountTotal = mappedItems.reduce((sum, it) => sum + toNum(it.discount), 0);
        const quoteDiscount = toNum(quotationData.discountTotal);
        const extra = quoteDiscount - lineDiscountTotal;
        if (extra > 0) {
          setBillDiscount(extra);
        }
      }
    }
  }, [quotationData, isView, defaultVat, vatIncluded, company, currentUser]);

  // For new sales, while the user hasn't manually edited payments,
  // keep the single payment row synced to the full grand total (or empty for credit).
  useEffect(() => {
    if (
      !isView &&
      payments.length === 1 &&
      autoPaymentAmount
    ) {
      const target = formData.salesType === 'cash'
        ? totalAmount.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))
        : '';
      
      if (payments[0].amount !== target) {
        const next = [...payments];
        next[0] = { ...next[0], amount: target };
        setPayments(next);
      }
    }
  }, [totalAmount, formData.salesType, isView, payments, autoPaymentAmount, company]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isView) {
      navigate('/sales');
      return;
    }
    if (company?.enableSalesSession === true && !registerOpen) {
      alert('Please Open the Register Session first before creating sales.');
      setOpenRegisterDialogOpen(true);
      return;
    }
    const mode = submitModeRef.current;
    submitModeRef.current = 'save';
    const isCreateAndHold = mode === 'save-hold';
    if (!isCreateAndHold) {
      if (!formData.customerId) {
        alert('Customer is required');
        return;
      }
      if (!formData.invoiceDate) {
        alert('Invoice Date is required');
        return;
      }
      if (!items.length) {
        alert('Please add at least one item');
        return;
      }
      if (items.some((i) => !i.productId)) {
        alert('Please select a product for all items');
        return;
      }
      const paidTotal = payments.reduce((sum, p) => sum + toNum(p.amount), 0);
      if (formData.salesType === 'cash' && paidTotal + 0.0001 < totalAmount) {
        alert('For cash sales, total payments must equal the total amount.');
        return;
      }
    } else {
      if (!formData.invoiceDate) {
        alert('Invoice Date is required');
        return;
      }
      if (!items.length) {
        alert('Please add at least one item');
        return;
      }
      if (items.some((i) => !i.productId)) {
        alert('Please select a product for all items');
        return;
      }
    }

    const globalMaxType = company?.maxDiscountType === 'percent' || company?.maxDiscountType === 'amount' ? company.maxDiscountType : null;
    const globalMaxPct = company?.maxDiscountPercent != null ? Number(company.maxDiscountPercent) : null;
    const globalMaxAmt = company?.maxDiscountAmount != null ? Number(company.maxDiscountAmount) : null;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const isRet = !!item.isReturn;
      if (!isRet) {
        const costPrice = Number(item.costPrice || 0);
        const sellingPrice = Number(item.unitPrice || 0);
        // Validation removed as per request
      }
      const lineMaxType =
        item.maxDiscountType === 'percent' || item.maxDiscountType === 'amount' ? item.maxDiscountType : globalMaxType;
      const lineMaxPct = item.maxDiscountPercent != null ? Number(item.maxDiscountPercent) : globalMaxPct;
      const lineMaxAmt = item.maxDiscountAmount != null ? Number(item.maxDiscountAmount) : globalMaxAmt;
      const discount = toNum(item.discount);
      if (lineMaxType === 'percent' && lineMaxPct != null && lineMaxPct > 0) {
        const qty = toNum(item.quantity);
        const price = toNum(item.unitPrice);
        const lineTotalBeforeDiscount = qty * price;
        if (lineTotalBeforeDiscount > 0 && discount > 0) {
          const lineDiscountPct = (discount / lineTotalBeforeDiscount) * 100;
          if (lineDiscountPct > lineMaxPct) {
            alert(
              `Maximum discount is ${lineMaxPct}%. Line ${i + 1} (${item.productName || 'item'}) has ${lineDiscountPct.toFixed(1)}% discount.`
            );
            return;
          }
        }
      }
      if (lineMaxType === 'amount' && lineMaxAmt != null && lineMaxAmt >= 0 && discount > lineMaxAmt) {
        alert(
          `Maximum discount per line is ${lineMaxAmt} ${currency}. Line ${i + 1} (${item.productName || 'item'}) has discount ${discount}.`
        );
        return;
      }
    }

    try {
      if (mode === 'save-hold') {
        const created = await createHold({
          ...formData,
          invoiceDate: formData.invoiceDate,
          orderDate: formData.orderDate || formData.invoiceDate,
          salesType: formData.salesType,
          creditDays:
            formData.salesType === 'credit'
              ? (formData.creditDays ? parseInt(formData.creditDays, 10) : undefined)
              : undefined,
          salesPersonId: formData.salesPersonId || undefined,
          locationId: formData.locationId || undefined,
          billDiscount: Number(billDiscount) || 0,
          payments: payments.map((p) => ({
            method: p.method,
            paymentAccountId: p.paymentAccountId || undefined,
            referenceNo: p.referenceNo || undefined,
            amount: Number(p.amount || 0),
          })),
          items: items.map((item) => {
            const { vatAmount } = computeLine(item);
            const computedVatPct = toNum(item.vatPercent) || defaultVat;
            const submitUnitPrice = vatIncluded && computedVatPct > 0
              ? parseFloat(item.unitPrice) * (1 + computedVatPct / 100)
              : parseFloat(item.unitPrice);

            return {
              productId: item.productId,
              quantity: parseFloat(item.quantity),
              unit: item.unit || undefined,
              unitPrice: submitUnitPrice,
              discount: toNum(item.discount),
              vatPercent: toNum(item.vatPercent) || defaultVat,
              vatAmount,
              batchNo: item.batchNo || undefined,
              expiryDate: item.expiryDate || undefined,
              serviceData: item.serviceData ? JSON.stringify(item.serviceData) : null,
              serialNumbers: item.serialNumbers || [],
              imeis: item.requiresImei ? item.imeis : undefined,
            };
          }),
        }).unwrap();

        if (!sessionUninitialized) refetchSession();

        if (company?.stayOnSalesFormAfterSave && !isView) {
          alert('Sales order placed on hold successfully!');
          resetForm();
          return;
        }

        navigate(created?.id ? `/sales/holds/${created.id}` : '/sales/holds');
        return;
      }
      const created = await createSalesOrder({
        ...formData,
        invoiceNumber: formData.invoiceNumber?.trim() || undefined,
        invoiceDate: formData.invoiceDate,
        orderDate: formData.orderDate || formData.invoiceDate,
        salesType: formData.salesType,
        creditDays:
          formData.salesType === 'credit'
            ? (formData.creditDays ? parseInt(formData.creditDays, 10) : undefined)
            : undefined,
        salesPersonId: formData.salesPersonId || undefined,
        locationId: formData.locationId || undefined,
        billDiscount: Number(billDiscount) || 0,
        payments: payments.map((p) => ({
          method: p.method,
          paymentAccountId: p.paymentAccountId || undefined,
          referenceNo: p.referenceNo || undefined,
          amount: Number(p.amount || 0),
        })),
        items: items.map((item) => {
          const { vatAmount, lineTotal } = computeLine(item);
          const computedVatPct = toNum(item.vatPercent) || defaultVat;
          const submitUnitPrice = vatIncluded && computedVatPct > 0
            ? parseFloat(item.unitPrice) * (1 + computedVatPct / 100)
            : parseFloat(item.unitPrice);

          return {
            productId: item.productId,
            quantity: parseFloat(item.quantity),
            unit: item.unit || undefined,
            unitPrice: submitUnitPrice,
            discount: toNum(item.discount),
            vatPercent: toNum(item.vatPercent) || defaultVat,
            vatAmount,
            batchNo: item.batchNo || undefined,
            expiryDate: item.expiryDate || undefined,
            isReturn: !!item.isReturn,
            isDamaged: !!item.isDamaged,
            damageReason: item.damageReason || undefined,
            serviceData: item.serviceData ? JSON.stringify(item.serviceData) : null,
            serialNumbers: item.serialNumbers || [],
            imeis: item.requiresImei ? item.imeis : undefined,
          };
        }),
      }).unwrap();

      if (!sessionUninitialized) refetchSession();

      if (mode === 'save-print' && created?.id) {
        if (company?.enableAutoPrintOnSave) {
          // Trigger print using hidden iframe without redirecting parent window
          const existingIframe = document.getElementById('silent-print-iframe');
          if (existingIframe) {
            existingIframe.remove();
          }
          const iframe = document.createElement('iframe');
          iframe.id = 'silent-print-iframe';
          iframe.style.position = 'absolute';
          iframe.style.left = '-9999px';
          iframe.style.top = '-9999px';
          iframe.style.width = '1024px';
          iframe.style.height = '768px';
          iframe.style.border = 'none';
          iframe.style.opacity = '0';
          iframe.style.pointerEvents = 'none';
          iframe.src = `/sales/orders/${created.id}/print`;
          document.body.appendChild(iframe);

          if (company?.stayOnSalesFormAfterSave && !isView) {
            alert('Sales order created successfully!');
            resetForm();
          } else {
            navigate('/sales');
          }
        } else {
          // If auto-print is disabled, redirect user to the print page for preview
          navigate(`/sales/orders/${created.id}/print`);
        }
      } else {
        if (company?.stayOnSalesFormAfterSave && !isView) {
          alert('Sales order created successfully!');
          resetForm();
        } else {
          navigate('/sales');
        }
      }
    } catch (error) {
      console.log(error);

      alert(error?.data?.message || 'Error creating sales order');
    }
  };

  const handleSubmitRef = useRef(handleSubmit);
  useEffect(() => {
    handleSubmitRef.current = handleSubmit;
  }, [handleSubmit]);

  if (isView && loadingOrder) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Box sx={{ pb: 4 }}>
      {/* Top Header & Status Ribbon */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 1.25,
          border: '1px solid #E2E8F0',
          bgcolor: '#FFFFFF',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 1,
              bgcolor: 'primary.50',
              border: '1px solid',
              borderColor: 'primary.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'primary.main',
            }}
          >
            <PointOfSaleIcon fontSize="medium" />
          </Box>
          <Box>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 800, color: '#0F172A', lineHeight: 1.2 }}>
              {isView
                ? existingOrder?.invoiceNumber
                  ? `Invoice #${existingOrder.invoiceNumber}`
                  : 'Sales Order Details'
                : 'POS Terminal & Invoice'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              {isView ? `Order Date: ${formData.invoiceDate}` : 'Fast checkout, live barcode lookup & instant invoice generation'}
            </Typography>
          </Box>
        </Box>

        {/* Action & Status Ribbon */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          {!isView && (
            <>
              {/* Import from Quotation Button */}
              <Tooltip title="Import customer, items, and prices from a Quotation">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    triggerFetchQuotations({ limit: 50 });
                    setImportQuotationModalOpen(true);
                  }}
                  startIcon={<DescriptionIcon fontSize="small" />}
                  sx={{
                    borderRadius: 1,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: '#CBD5E1',
                    color: '#475569',
                    '&:hover': { bgcolor: '#F8FAFC', borderColor: '#94A3B8' },
                  }}
                >
                  Import Quotation
                </Button>
              </Tooltip>

              {/* Keyboard Shortcuts Trigger */}
              <Tooltip title="View all POS keyboard shortcuts">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setShortcutsOpen(true)}
                  startIcon={<KeyboardIcon fontSize="small" />}
                  sx={{
                    borderRadius: 1,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: '#CBD5E1',
                    color: '#475569',
                    '&:hover': { bgcolor: '#F8FAFC', borderColor: '#94A3B8' },
                  }}
                >
                  Shortcuts (F1-F11)
                </Button>
              </Tooltip>

              {/* Sales Register Session Badge */}
              {company?.enableSalesSession === true && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={
                      registerOpen
                        ? `Register Open${registerSession?.user?.firstName ? ` (${registerSession.user.firstName})` : ''}: ${Number(registerSession?.openingBalance || 0).toFixed(dec)} ${currency}`
                        : 'Register Closed'
                    }
                    color={registerOpen ? 'success' : 'error'}
                    variant="outlined"
                    sx={{ fontWeight: 700, borderRadius: 1 }}
                  />
                  {registerOpen ? (
                    <Button
                      variant="outlined"
                      color="warning"
                      size="small"
                      onClick={() => {
                        if (!sessionUninitialized) refetchSession();
                        setCloseRegisterDialogOpen(true);
                      }}
                      sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 600 }}
                    >
                      Close Shift
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={() => setOpenRegisterDialogOpen(true)}
                      sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 600 }}
                    >
                      Open Shift
                    </Button>
                  )}
                </Box>
              )}
            </>
          )}

          {isView && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <TextField
                select
                size="small"
                label="Status"
                value={existingOrder?.status ?? ''}
                onChange={(e) => {
                  const newStatus = e.target.value;
                  if (newStatus && id) updateSalesOrderStatus({ id, status: newStatus });
                }}
                sx={{ minWidth: 140 }}
              >
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="quotation">Quotation</MenuItem>
                <MenuItem value="on_hold">On hold</MenuItem>
                <MenuItem value="confirmed">Confirmed</MenuItem>
                <MenuItem value="shipped">Shipped</MenuItem>
                <MenuItem value="delivered">Delivered</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </TextField>
              <Button
                variant="outlined"
                startIcon={<PictureAsPdfIcon />}
                onClick={() => {
                  if (existingOrder && company) {
                    shareSalesOrderPdf(existingOrder, company, {
                      itemFormat: company.printItemNameFormat || 'name_sku',
                      filename: (existingOrder.invoiceNumber || existingOrder.orderNumber || 'invoice').replace(
                        /\s+/g,
                        '-'
                      ),
                    });
                  }
                }}
                disabled={!existingOrder || !company}
                sx={{ borderRadius: 1 }}
              >
                Share
              </Button>
              <Button
                variant="outlined"
                startIcon={<EmailIcon />}
                onClick={() => setEmailDialogOpen(true)}
                disabled={!existingOrder}
                sx={{ borderRadius: 1 }}
              >
                Email
              </Button>
              <Button
                variant="outlined"
                startIcon={<WhatsAppIcon />}
                onClick={() => setWhatsappDialogOpen(true)}
                disabled={!existingOrder}
                sx={{ borderRadius: 1, borderColor: '#25D366', color: '#128C7E', '&:hover': { borderColor: '#128C7E', bgcolor: 'rgba(37, 211, 102, 0.04)' } }}
              >
                WhatsApp
              </Button>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={() => {
                  if (company?.enableAutoPrintOnSave) {
                    const existingIframe = document.getElementById('silent-print-iframe');
                    if (existingIframe) {
                      existingIframe.remove();
                    }
                    const iframe = document.createElement('iframe');
                    iframe.id = 'silent-print-iframe';
                    iframe.style.position = 'absolute';
                    iframe.style.left = '-9999px';
                    iframe.style.top = '-9999px';
                    iframe.style.width = '1024px';
                    iframe.style.height = '768px';
                    iframe.style.border = 'none';
                    iframe.style.opacity = '0';
                    iframe.style.pointerEvents = 'none';
                    iframe.src = `/sales/orders/${id}/print`;
                    document.body.appendChild(iframe);
                  } else {
                    navigate(`/sales/orders/${id}/print`);
                  }
                }}
                sx={{ borderRadius: 1 }}
              >
                Print Invoice
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={!existingOrder || existingOrder?.status === 'on_hold' || Number(existingOrder?.balanceAmount || 0) <= 0}
                title={existingOrder?.status === 'on_hold' ? 'Unhold the order first to record payment' : ''}
                onClick={() => {
                  const balance = Number(existingOrder?.balanceAmount || 0) || 0;
                  setReceivePaymentForm((prev) => ({
                    ...prev,
                    paymentDate: getLocalDateString(),
                    amount: balance > 0 ? balance.toFixed(dec) : '',
                    paymentMethod: 'cash',
                    paymentAccountId: '',
                    referenceNo: '',
                    notes: '',
                  }));
                  setReceivePaymentOpen(true);
                }}
                sx={{ borderRadius: 1 }}
              >
                Receive Payment
              </Button>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Quotation Converted Alert Banner */}
      {quotationData && !isView && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 1.25,
            bgcolor: '#EFF6FF',
            border: '1px solid #BFDBFE',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1,
                bgcolor: '#DBEAFE',
                color: '#1D4ED8',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <DescriptionIcon />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E40AF' }}>
                  Converted from Quotation #{quotationData.quotationNumber}
                </Typography>
                <Chip
                  label="Quotation Linked"
                  color="primary"
                  size="small"
                  sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700, borderRadius: 1 }}
                />
              </Box>
              <Typography variant="body2" sx={{ color: '#3B82F6' }}>
                Customer: <strong>{quotationData.customer?.name || 'Walk-in'}</strong> &bull; {quotationData.items?.length || 0} line item(s) pre-filled into this sales order. Review or edit below and save to complete.
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => window.open(`/quotations/${quotationData.id}`, '_blank')}
              sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 600, borderColor: '#93C5FD', color: '#1D4ED8' }}
            >
              View Original Quote
            </Button>
            <Button
              size="small"
              color="inherit"
              onClick={() => {
                setActiveQuotationId('');
                setSearchParams({});
              }}
              sx={{ borderRadius: 1, textTransform: 'none', color: '#64748B' }}
            >
              Clear Link
            </Button>
          </Box>
        </Paper>
      )}

      {/* Keyboard Shortcut Reference Bar */}
      {!isView && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 2,
            overflowX: 'auto',
            pb: 0.5,
            '&::-webkit-scrollbar': { height: 4 },
            '&::-webkit-scrollbar-thumb': { bgcolor: '#CBD5E1', borderRadius: 1 },
          }}
        >
          {[
            { key: 'F1', label: 'Scan Item', color: '#3B82F6' },
            { key: 'F4', label: 'Save & Print', color: '#10B981' },
            { key: 'F2', label: 'Create Order', color: '#6366F1' },
            { key: 'F3', label: 'Hold Bill', color: '#F59E0B' },
            { key: 'F5', label: 'Customer', color: '#8B5CF6' },
            { key: 'F6', label: 'Cancel', color: '#EF4444' },
            { key: 'F7', label: 'Cost Margin', color: '#64748B' },
            { key: 'F11', label: 'Price History', color: '#64748B' },
          ].map((sc) => (
            <Chip
              key={sc.key}
              size="small"
              label={
                <span>
                  <strong style={{ color: sc.color }}>[{sc.key}]</strong> {sc.label}
                </span>
              }
              sx={{
                bgcolor: '#FFFFFF',
                border: '1px solid #E2E8F0',
                fontSize: '0.75rem',
                fontWeight: 500,
                borderRadius: 1,
                cursor: 'default',
              }}
            />
          ))}
        </Box>
      )}

      {/* Van Sales Mode Banner */}
      {vanMode && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 1.25,
            bgcolor: '#E3F2FD',
            border: '1px solid #90CAF9',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <LocalShippingIcon sx={{ color: '#1565C0', fontSize: 28 }} />
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1565C0' }}>
              Van Sales Mode
            </Typography>
            <Typography variant="body2" sx={{ color: '#1565C0' }}>
              Van: {vanName} ({vanPlate}) &bull; Trip: {vanTripNumber} &bull;
              Sales will be made from the van&apos;s stock and automatically linked to this trip.
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Main Workstation Layout */}
      <form ref={formRef} onSubmit={handleSubmit}>
        <Grid container spacing={2.5} alignItems="flex-start">
          {/* LEFT PANEL: Customer, Product Hub & Line Items Table */}
          <Grid size={{ xs: 12, lg: 8 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              
              {/* 1. Customer & Fast Order Hub */}
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 1.25,
                  border: '1px solid #E2E8F0',
                  bgcolor: '#FFFFFF',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0F172A', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StorefrontIcon fontSize="small" sx={{ color: 'primary.main' }} />
                    Customer &amp; Order Information
                  </Typography>
                </Box>

                {/* Customer Autocomplete & Quick Buttons */}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <Autocomplete
                    fullWidth
                    options={customerSearchOptions}
                    loading={searchingCustomers}
                    getOptionLabel={(opt) => opt?.name || ''}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    getOptionDisabled={(option) => !canWholesale && option?.customerType === 'wholesale'}
                    value={selectedCustomer}
                    onChange={(_, newValue) => {
                      if (newValue?.customerType === 'wholesale' && !canWholesale) {
                        return;
                      }
                      setFormData({ ...formData, customerId: newValue?.id || '' });
                      if (newValue?.customerType === 'wholesale' && canWholesale) {
                        handlePricingModeChange('wholesale');
                      } else if (newValue?.customerType === 'retail' && pricingMode === 'wholesale') {
                        handlePricingModeChange('retail');
                      }
                    }}
                    onInputChange={(_, newInputValue, reason) => {
                      if (reason === 'input') {
                        if (newInputValue && newInputValue.length >= 2) {
                          triggerCustomerSearch({ search: newInputValue, limit: 20 });
                        } else if (!newInputValue) {
                          triggerCustomerSearch({ limit: 20 });
                        }
                      }
                    }}
                    onOpen={() => {
                      if (customerSearchOptions.length === 0) triggerCustomerSearch({ limit: 20 });
                    }}
                    disabled={isView}
                    renderOption={(props, option) => {
                      const { key, ...otherProps } = props;
                      const isRestrictedWholesale = !canWholesale && option.customerType === 'wholesale';
                      return (
                        <li key={key || option.id} {...otherProps} style={{ opacity: isRestrictedWholesale ? 0.5 : 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                            <span>{option.name}</span>
                            {option.customerType === 'wholesale' && (
                              <Chip
                                size="small"
                                label={canWholesale ? "Wholesale" : "Wholesale (Restricted)"}
                                color={canWholesale ? "secondary" : "default"}
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600, ml: 1, borderRadius: 1 }}
                              />
                            )}
                          </Box>
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        inputRef={customerSearchInputRef}
                        inputProps={{
                          ...params.inputProps,
                          id: 'customer-search-input',
                        }}
                        id="customer-search-input"
                        label="Customer Name * [F5]"
                        placeholder="Search customer by name or phone (Press F5)..."
                        required={!formData.customerId}
                      />
                    )}
                  />
                  {!isView && (
                    <>
                      <Button
                        variant="outlined"
                        onClick={() => setCustomerDialogOpen(true)}
                        sx={{ height: 56, minWidth: 44, borderRadius: 1 }}
                        title="Add New Customer"
                      >
                        <PersonAddAltIcon fontSize="small" />
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleCreateCashCustomer}
                        sx={{
                          height: 56,
                          px: 2,
                          borderRadius: 1,
                          backgroundColor: '#0D9488',
                          '&:hover': { backgroundColor: '#0F766E' },
                          color: 'white',
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          textTransform: 'none',
                        }}
                      >
                        + Cash Customer
                      </Button>
                    </>
                  )}
                </Box>

                {/* Selected Customer Info Strip */}
                {selectedCustomer && (
                  <Box
                    sx={{
                      mt: 1.5,
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: '#F8FAFC',
                      border: '1px solid #E2E8F0',
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1.5,
                    }}
                  >
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#0F172A' }}>
                        {selectedCustomer.name}
                      </Typography>
                      {selectedCustomer.phone && (
                        <Chip
                          size="small"
                          label={`📞 ${selectedCustomer.phone}`}
                          variant="outlined"
                          sx={{ height: 22, fontSize: '0.72rem', borderColor: '#CBD5E1', borderRadius: 1 }}
                        />
                      )}
                      {selectedCustomer.trn && (
                        <Chip
                          size="small"
                          label={`TRN: ${selectedCustomer.trn}`}
                          variant="outlined"
                          sx={{ height: 22, fontSize: '0.72rem', borderColor: '#CBD5E1', borderRadius: 1 }}
                        />
                      )}
                      {selectedCustomer.customerType === 'wholesale' && (
                        <Chip
                          size="small"
                          label="🏢 Wholesale Customer"
                          color="secondary"
                          sx={{ height: 22, fontSize: '0.72rem', fontWeight: 600, borderRadius: 1 }}
                        />
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                      {Number(selectedCustomer.balance || 0) > 0 && (
                        <Chip
                          size="small"
                          color="error"
                          variant="outlined"
                          label={`⚠️ Outstanding Due: ${formatAmount(selectedCustomer.balance)}`}
                          sx={{ height: 24, fontSize: '0.75rem', fontWeight: 700, borderRadius: 1 }}
                        />
                      )}
                      {Number(selectedCustomer.advanceBalance || 0) > 0 && (
                        <Chip
                          size="small"
                          color="success"
                          onClick={() => handleQuickMethod('advance')}
                          title="Click to use advance balance as payment"
                          label={`💰 Advance: ${formatAmount(selectedCustomer.advanceBalance)} (Click to Use)`}
                          sx={{ height: 24, fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', borderRadius: 1 }}
                        />
                      )}
                      {selectedCustomer.creditLimit && Number(selectedCustomer.creditLimit) > 0 && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                          Credit Limit: {formatAmount(selectedCustomer.creditLimit)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}

                {/* Collapsible Invoice Metadata Details */}
                <Accordion
                  defaultExpanded={false}
                  elevation={0}
                  sx={{
                    mt: 1.5,
                    border: '1px solid #E2E8F0',
                    borderRadius: '6px !important',
                    '&:before': { display: 'none' },
                    bgcolor: '#FAFAFA',
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Invoice Settings &amp; Store Metadata (Click to expand)
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0, pb: 2 }}>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Invoice Number"
                          name="invoiceNumber"
                          value={!isView && !formData.invoiceNumber ? previewInvoiceNumber : formData.invoiceNumber}
                          disabled
                          helperText={isView ? 'Invoice number' : 'Auto-generated on save'}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <TextField
                          fullWidth
                          size="small"
                          type="date"
                          label="Invoice Date *"
                          name="invoiceDate"
                          value={formData.invoiceDate}
                          onChange={(e) => setFormData({ ...formData, invoiceDate: e.target.value })}
                          InputLabelProps={{ shrink: true }}
                          required
                          disabled={isView}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <TextField
                          fullWidth
                          size="small"
                          select
                          label="Sales Type"
                          name="salesType"
                          value={formData.salesType}
                          onChange={(e) => {
                            const next = e.target.value;
                            setFormData((prev) => ({
                              ...prev,
                              salesType: next,
                              creditDays: next === 'credit' ? prev.creditDays : '',
                            }));
                          }}
                          disabled={isView}
                        >
                          <MenuItem value="cash">Cash</MenuItem>
                          <MenuItem value="credit">Credit</MenuItem>
                        </TextField>
                      </Grid>
                      {formData.salesType === 'credit' && (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                          <TextField
                            fullWidth
                            size="small"
                            select
                            label="Credit days (due)"
                            name="creditDays"
                            value={formData.creditDays}
                            onChange={(e) => setFormData({ ...formData, creditDays: e.target.value })}
                            disabled={isView}
                            helperText="Payment due window"
                          >
                            <MenuItem value="">None</MenuItem>
                            {creditDaysOptions.map((days) => (
                              <MenuItem key={days} value={String(days)}>
                                {days} days
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      )}
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <TextField
                          fullWidth
                          size="small"
                          select
                          label="Sales Person"
                          name="salesPersonId"
                          value={formData.salesPersonId}
                          onChange={(e) => setFormData({ ...formData, salesPersonId: e.target.value })}
                          disabled={isView}
                        >
                          <MenuItem value="">None</MenuItem>
                          {userList.map((u) => (
                            <MenuItem key={u.id} value={u.id}>
                              {u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.email || u.username}
                            </MenuItem>
                          ))}
                          {formData.salesPersonId && !userList.find((u) => u.id === formData.salesPersonId) && (
                            <MenuItem key={formData.salesPersonId} value={formData.salesPersonId}>
                              Unknown User
                            </MenuItem>
                          )}
                        </TextField>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <TextField
                          fullWidth
                          size="small"
                          select
                          label={vanMode ? `Branch (Van: ${vanName})` : 'Branch / Store'}
                          name="locationId"
                          value={formData.locationId}
                          onChange={(e) => setFormData({ ...formData, locationId: e.target.value })}
                          disabled={isView || !!vanMode}
                        >
                          <MenuItem value="">None</MenuItem>
                          {locations?.map((loc) => (
                            <MenuItem key={loc.id} value={loc.id}>
                              {loc.name} ({loc.code})
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Paper>

              {/* 2. Product Barcode & Fast Scanner Hub */}
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 1.25,
                  border: '1.5px solid #CBD5E1',
                  bgcolor: '#FFFFFF',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0F172A', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ShoppingCartIcon fontSize="small" sx={{ color: 'primary.main' }} />
                    Product &amp; Barcode Scanner
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {!isView && canWholesale && (
                      <ToggleButtonGroup
                        value={pricingMode}
                        exclusive
                        onChange={(e, newMode) => {
                          if (newMode) handlePricingModeChange(newMode);
                        }}
                        size="small"
                        sx={{
                          height: 34,
                          '& .MuiToggleButton-root': {
                            px: 1.5,
                            py: 0.5,
                            fontWeight: 700,
                            textTransform: 'none',
                            fontSize: '0.8rem',
                            borderRadius: '4px !important',
                          },
                          '& .Mui-selected': {
                            bgcolor: pricingMode === 'wholesale' ? '#6C5CE7 !important' : '#1976D2 !important',
                            color: '#fff !important',
                          },
                        }}
                      >
                        <ToggleButton value="retail">🏷️ Retail Price</ToggleButton>
                        <ToggleButton value="wholesale">🏢 Wholesale Price</ToggleButton>
                      </ToggleButtonGroup>
                    )}

                    {!isView && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => setProductDialogOpen(true)}
                        sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 600, height: 34 }}
                      >
                        New Product
                      </Button>
                    )}
                  </Box>
                </Box>

                {!isView && (
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                    {/* Quick Price Override Input Box */}
                    {company?.enableQuickScanPrice !== false && (
                      <Tooltip title="Type custom price, then scan barcode to apply automatically (Alt+P)" arrow>
                        <TextField
                          size="medium"
                          type="number"
                          id="quick-scan-price-input"
                          inputRef={quickPriceInputRef}
                          label={`Price (${currency})`}
                          placeholder="0.00"
                          value={quickScanPrice}
                          onChange={(e) => setQuickScanPrice(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              focusProductSearch();
                            }
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Typography variant="caption" sx={{ fontWeight: 800, color: 'primary.main', fontSize: '0.8rem' }}>
                                  {currency}
                                </Typography>
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end" sx={{ gap: 0.25 }}>
                                {quickScanPrice !== '' && (
                                  <IconButton size="small" onClick={() => setQuickScanPrice('')} sx={{ p: 0.25 }} title="Clear Price">
                                    <CloseIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                )}
                                <Tooltip title={lockQuickScanPrice ? 'Price Locked (applies to all scans)' : 'Price Unlocked (clears after single scan)'} arrow>
                                  <IconButton
                                    size="small"
                                    onClick={() => setLockQuickScanPrice(!lockQuickScanPrice)}
                                    color={lockQuickScanPrice ? 'primary' : 'default'}
                                    sx={{ p: 0.25 }}
                                  >
                                    {lockQuickScanPrice ? <LockIcon sx={{ fontSize: 16 }} /> : <LockOpenIcon sx={{ fontSize: 16 }} />}
                                  </IconButton>
                                </Tooltip>
                              </InputAdornment>
                            ),
                          }}
                          inputProps={{
                            min: 0,
                            step: 0.01,
                          }}
                          sx={{
                            width: { xs: '100%', sm: 165 },
                            flexShrink: 0,
                            '& .MuiInputBase-root': {
                              height: 50,
                              borderRadius: 1,
                              fontWeight: 700,
                              fontSize: '0.95rem',
                              bgcolor: quickScanPrice ? '#f0fdf4' : 'background.paper',
                              '& fieldset': {
                                borderColor: quickScanPrice ? '#22c55e' : undefined,
                                borderWidth: quickScanPrice ? '2px' : undefined,
                              },
                            },
                          }}
                        />
                      </Tooltip>
                    )}

                    <Autocomplete
                      fullWidth
                      size="medium"
                      autoHighlight
                      freeSolo
                      options={searchingProducts ? [] : (productSearchData?.products || [])}
                      loading={searchingProducts}
                      filterOptions={(x) => x}
                      value={null}
                      inputValue={productSearch}
                      getOptionLabel={(option) => {
                        if (!option) return '';
                        if (typeof option === 'string') return option;
                        const label = formatItemDisplayName(
                          { name: option.name, sku: option.sku || option.barcode, brand: option.brand },
                          itemNameFormat === 'name' ? 'name_sku' : itemNameFormat
                        );
                        return label || '';
                      }}
                      renderOption={(props, option) => {
                        const { key, ...otherProps } = props;
                        const isWholesale = pricingMode === 'wholesale';
                        return (
                          <li key={key || option.id} {...otherProps}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', py: 0.5 }}>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {formatItemDisplayName(
                                    { name: option.name, sku: option.sku || option.barcode, brand: option.brand },
                                    itemNameFormat === 'name' ? 'name_sku' : itemNameFormat
                                  )}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Stock: {option.stock ?? option.quantity ?? 0} {option.unit || 'pcs'}
                                </Typography>
                              </Box>
                              <Box sx={{ textAlign: 'right', display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: isWholesale ? 400 : 700,
                                    color: isWholesale ? 'text.secondary' : 'primary.main',
                                  }}
                                >
                                  RT: {currency} {Number(option.sellingPrice || 0).toFixed(dec)}
                                </Typography>
                                {canWholesale && Number(option.wholesalePrice) > 0 && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontWeight: isWholesale ? 700 : 400,
                                      color: isWholesale ? 'secondary.main' : 'text.secondary',
                                    }}
                                  >
                                    WS: {currency} {Number(option.wholesalePrice || 0).toFixed(dec)}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </li>
                        );
                      }}
                      onInputChange={(_, value) => {
                        setProductSearch(value);
                        if (value && value.trim().length >= 1) {
                          triggerProductSearch({ search: value.trim(), limit: 20 });
                        }
                      }}
                      onChange={(_, value) => {
                        if (value && typeof value === 'object') {
                          handleAddProductRow(value);
                          setProductSearch('');
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          inputRef={productSearchInputRef}
                          inputProps={{
                            ...params.inputProps,
                            id: 'product-search-input',
                          }}
                          id="product-search-input"
                          autoFocus
                          label="Scan Barcode or Search Item [F1]"
                          placeholder="Type item name, SKU, or scan barcode (Press Enter or F1)..."
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const list = searchingProducts ? [] : (productSearchData?.products || []);
                              if (list.length > 0) {
                                const typed = String(productSearch).trim();
                                const firstItem = list[0];
                                if (typed) {
                                  const isExactMatch =
                                    String(firstItem.barcode || '').trim() === typed ||
                                    String(firstItem.sku || '').trim() === typed ||
                                    firstItem.name.toLowerCase().includes(typed.toLowerCase());
                                  if (!isExactMatch) return;
                                }
                                handleAddProductRow(firstItem);
                                setProductSearch('');
                              }
                            }
                          }}
                          sx={{
                            '& .MuiInputBase-root': {
                              height: 50,
                              borderRadius: 1,
                              fontSize: '0.95rem',
                            },
                          }}
                        />
                      )}
                    />
                    <IconButton
                      onClick={() => setScanOpen(true)}
                      title="Scan barcode with camera"
                      sx={{
                        height: 50,
                        width: 50,
                        borderRadius: 1,
                        border: '1px solid #CBD5E1',
                        bgcolor: '#F8FAFC',
                        '&:hover': { bgcolor: '#F1F5F9' },
                      }}
                    >
                      <QrCodeScannerIcon color="primary" />
                    </IconButton>
                  </Box>
                )}
              </Paper>

              {/* 3. Live Line Items Table */}
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 1.25,
                  border: '1px solid #E2E8F0',
                  bgcolor: '#FFFFFF',
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ p: 2, bgcolor: '#F8FAFC', borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0F172A' }}>
                      Cart Items
                    </Typography>
                    <Chip
                      label={`${items.length} ${items.length === 1 ? 'item' : 'items'}`}
                      size="small"
                      color="primary"
                      sx={{ fontWeight: 700, height: 22, fontSize: '0.72rem', borderRadius: 1 }}
                    />
                  </Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    Total Qty: {totalQty}
                  </Typography>
                </Box>

                {items.length === 0 ? (
                  <Box
                    sx={{
                      py: 8,
                      px: 3,
                      textAlign: 'center',
                      bgcolor: '#FFFFFF',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 1.5,
                        bgcolor: '#F1F5F9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#94A3B8',
                      }}
                    >
                      <ShoppingCartIcon sx={{ fontSize: 28 }} />
                    </Box>
                    <Typography variant="h6" sx={{ color: '#475569', fontWeight: 700 }}>
                      Your Cart is Empty
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#94A3B8', maxWidth: 400 }}>
                      Scan a product barcode or press <strong>[F1]</strong> to search and add items to this invoice.
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                          <TableCell sx={{ fontWeight: 700, width: 45, color: '#475569' }}>#</TableCell>
                          <TableCell sx={{ fontWeight: 700, minWidth: 200, color: '#475569' }}>Product Details</TableCell>
                          <TableCell sx={{ fontWeight: 700, width: 120, color: '#475569' }}>Type</TableCell>
                          <TableCell sx={{ fontWeight: 700, width: 110, color: '#475569' }} align="center">Qty</TableCell>
                          <TableCell sx={{ fontWeight: 700, width: 110, color: '#475569' }}>{isTaxEnabled ? 'Inc. Price' : 'Unit Price'}</TableCell>
                          <TableCell sx={{ fontWeight: 700, width: 75, color: '#475569' }}>Disc.</TableCell>
                          {isTaxEnabled && <TableCell sx={{ fontWeight: 700, width: 65, color: '#475569' }}>VAT %</TableCell>}
                          {isTaxEnabled && <TableCell sx={{ fontWeight: 700, width: 85, color: '#475569' }}>VAT Amt</TableCell>}
                          <TableCell sx={{ fontWeight: 700, width: 100, color: '#475569' }}>Total</TableCell>
                          {!isView && <TableCell sx={{ fontWeight: 700, width: 50, color: '#475569' }} align="center">Act</TableCell>}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {items.map((item, index) => {
                          const { vatAmount, lineTotalMixed } = computeLine(item);
                          return (
                            <TableRow
                              key={index}
                              hover
                              className="sales-item-row"
                              onClick={(e) => {
                                activeRowIndexRef.current = index;
                                document.querySelectorAll('.sales-item-row').forEach((el) => el.classList.remove('Mui-selected'));
                                e.currentTarget.classList.add('Mui-selected');
                              }}
                              onFocusCapture={(e) => {
                                activeRowIndexRef.current = index;
                                document.querySelectorAll('.sales-item-row').forEach((el) => el.classList.remove('Mui-selected'));
                                e.currentTarget.closest('.sales-item-row')?.classList.add('Mui-selected');
                              }}
                              sx={{
                                '&.Mui-selected': { bgcolor: '#F0F9FF !important' },
                              }}
                            >
                              <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>{index + 1}</TableCell>
                              <TableCell>
                                <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: '#0F172A' }}>
                                  {item.productName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {item.productSku} {item.productBrand ? `• ${item.productBrand}` : ''}
                                </Typography>
                                {item.hasWarranty && warrantyPeriodsData && (
                                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'success.main', fontWeight: 600 }}>
                                    🛡️ Warranty: {warrantyPeriodsData.find((wp) => wp.id === item.warrantyPeriodId)?.name || 'Enabled'}
                                  </Typography>
                                )}
                                {item.serialNumbers && Array.isArray(item.serialNumbers) && item.serialNumbers.filter((s) => s).length > 0 && (
                                  <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: 'text.secondary' }}>
                                    SN: {item.serialNumbers.filter((s) => s).join(', ')}
                                  </Typography>
                                )}
                                {company?.enableServiceModule && item.productType === 'service' && (
                                  <Box sx={{ mt: 1, p: 1.5, borderRadius: 1, bgcolor: '#FBF8FF', border: '1px solid #E1D5E7', maxWidth: 400 }}>
                                    <Typography variant="caption" color="secondary" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                                      🔧 Service / Repair Details
                                    </Typography>
                                    <Grid container spacing={1}>
                                      <Grid size={{ xs: 6 }}>
                                        <TextField
                                          size="small"
                                          label="Device Model"
                                          placeholder="e.g. iPhone 14 Pro"
                                          value={item.serviceData?.deviceModel || ''}
                                          disabled={isView}
                                          onChange={(e) => {
                                            const currentServiceData = item.serviceData || {};
                                            handleItemChange(index, 'serviceData', {
                                              ...currentServiceData,
                                              deviceModel: e.target.value,
                                            });
                                          }}
                                          slotProps={{ input: { style: { fontSize: '0.75rem' } }, inputLabel: { style: { fontSize: '0.75rem' } } }}
                                          fullWidth
                                        />
                                      </Grid>
                                      <Grid size={{ xs: 6 }}>
                                        <TextField
                                          size="small"
                                          label="IMEI / Serial"
                                          placeholder="IMEI or Serial No"
                                          value={item.serviceData?.imei || ''}
                                          disabled={isView}
                                          onChange={(e) => {
                                            const currentServiceData = item.serviceData || {};
                                            handleItemChange(index, 'serviceData', {
                                              ...currentServiceData,
                                              imei: e.target.value,
                                            });
                                          }}
                                          slotProps={{ input: { style: { fontSize: '0.75rem' } }, inputLabel: { style: { fontSize: '0.75rem' } } }}
                                          fullWidth
                                        />
                                      </Grid>
                                      <Grid size={{ xs: 12 }}>
                                        <TextField
                                          size="small"
                                          label="Fault / Complains"
                                          placeholder="Describe issues / diagnostic notes"
                                          value={item.serviceData?.complains || ''}
                                          disabled={isView}
                                          onChange={(e) => {
                                            const currentServiceData = item.serviceData || {};
                                            handleItemChange(index, 'serviceData', {
                                              ...currentServiceData,
                                              complains: e.target.value,
                                            });
                                          }}
                                          slotProps={{ input: { style: { fontSize: '0.75rem' } }, inputLabel: { style: { fontSize: '0.75rem' } } }}
                                          fullWidth
                                          multiline
                                          rows={1}
                                        />
                                      </Grid>
                                    </Grid>
                                  </Box>
                                )}
                                {item.hasWarranty && (
                                  <Box sx={{ mt: 1 }}>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color={item.serialNumbers?.length === Number(item.quantity) ? 'success' : 'primary'}
                                      onClick={() => setActiveSerialIndex(index)}
                                      sx={{ fontSize: '0.72rem', py: 0.25, borderRadius: 1 }}
                                    >
                                      {item.serialNumbers?.length > 0
                                        ? `SN: ${item.serialNumbers.length}/${item.quantity}`
                                        : '+ Serial Numbers'}
                                    </Button>
                                  </Box>
                                )}
                                {item.requiresImei && (
                                  <Button
                                    size="small"
                                    variant={item.imeis?.length === parseFloat(item.quantity) * (item.requiresDualImei ? 2 : 1) ? 'contained' : 'outlined'}
                                    color={item.imeis?.length === parseFloat(item.quantity) * (item.requiresDualImei ? 2 : 1) ? 'success' : 'warning'}
                                    onClick={() =>
                                      setImeiModal({
                                        open: true,
                                        itemIndex: index,
                                        imeis: item.imeis || [],
                                        text: (item.imeis || []).join('\n'),
                                      })
                                    }
                                    sx={{ mr: 1, mt: 1, fontSize: '0.72rem', py: 0.25, borderRadius: 1 }}
                                  >
                                    {item.imeis?.length || 0} / {parseFloat(item.quantity) * (item.requiresDualImei ? 2 : 1) || 0} IMEI
                                  </Button>
                                )}
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        size="small"
                                        checked={!!item.isReturn}
                                        onChange={(e) => handleItemChange(index, 'isReturn', e.target.checked)}
                                        disabled={isView}
                                      />
                                    }
                                    label={<Typography variant="caption" sx={{ fontWeight: item.isReturn ? 700 : 400, color: item.isReturn ? 'error.main' : 'inherit' }}>Return</Typography>}
                                  />
                                  {item.isReturn && (
                                    <>
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            size="small"
                                            color="error"
                                            checked={!!item.isDamaged}
                                            onChange={(e) => handleItemChange(index, 'isDamaged', e.target.checked)}
                                            disabled={isView}
                                          />
                                        }
                                        label={<Typography variant="caption" color="error" sx={{ fontWeight: 600 }}>Damaged</Typography>}
                                      />
                                      {item.isDamaged && (
                                        <TextField
                                          placeholder="Reason"
                                          size="small"
                                          value={item.damageReason || ''}
                                          onChange={(e) => handleItemChange(index, 'damageReason', e.target.value)}
                                          fullWidth
                                          disabled={isView}
                                          sx={{
                                            '& .MuiInputBase-input': { fontSize: '0.72rem', p: '3px 6px' },
                                          }}
                                        />
                                      )}
                                    </>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell align="center">
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      const current = Number(item.quantity || 0) || 0;
                                      const next = Math.max(0.01, current - 1);
                                      handleItemChange(index, 'quantity', String(next));
                                    }}
                                    disabled={isView}
                                    sx={{ p: 0.5 }}
                                  >
                                    <RemoveIcon fontSize="small" />
                                  </IconButton>
                                  <TextField
                                    id={`qty-input-${index}`}
                                    type="number"
                                    size="small"
                                    value={item.quantity}
                                    onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                    sx={{ width: 56, '& input': { textAlign: 'center', p: '4px 6px', fontWeight: 600 } }}
                                    inputProps={{ min: 0.01, step: 0.01 }}
                                    disabled={isView}
                                    required
                                  />
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      const current = Number(item.quantity || 0) || 0;
                                      const next = Math.max(0.01, current + 1);
                                      handleItemChange(index, 'quantity', String(next));
                                    }}
                                    disabled={isView}
                                    sx={{ p: 0.5 }}
                                  >
                                    <AddIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <TextField
                                    id={`price-input-${index}`}
                                    type="number"
                                    size="small"
                                    value={
                                      item.inclusivePriceInput !== undefined
                                        ? item.inclusivePriceInput
                                        : item.unitPrice === ''
                                        ? ''
                                        : Number((toNum(item.unitPrice) * (1 + toNum(item.vatPercent) / 100)).toFixed(dec)).toString()
                                    }
                                    onChange={(e) => {
                                      const nextInclusive = e.target.value;
                                      handleItemChange(index, 'inclusivePriceInput', nextInclusive);
                                      if (nextInclusive === '') {
                                        handleItemChange(index, 'unitPrice', '');
                                      } else {
                                        const exclusivePrice = Number(nextInclusive) / (1 + toNum(item.vatPercent) / 100);
                                        handleItemChange(index, 'unitPrice', exclusivePrice.toFixed(dec));
                                      }
                                    }}
                                    sx={{ width: 85, '& input': { p: '4px 6px' } }}
                                    inputProps={{ min: 0, step: 0.01 }}
                                    disabled={isView}
                                  />
                                  {!isView && canWholesale && Number(item.wholesalePrice) > 0 && (
                                    <Tooltip
                                      title={
                                        item.pricingType === 'wholesale'
                                          ? `Wholesale Active. Click to switch to Retail (${currency} ${Number(item.sellingPrice || 0).toFixed(dec)})`
                                          : `Retail Active. Click to switch to Wholesale (${currency} ${Number(item.wholesalePrice || 0).toFixed(dec)})`
                                      }
                                    >
                                      <Chip
                                        size="small"
                                        label={item.pricingType === 'wholesale' ? 'WS' : 'RT'}
                                        color={item.pricingType === 'wholesale' ? 'secondary' : 'default'}
                                        variant={item.pricingType === 'wholesale' ? 'filled' : 'outlined'}
                                        onClick={() => {
                                          const nextType = item.pricingType === 'wholesale' ? 'retail' : 'wholesale';
                                          const targetBase =
                                            nextType === 'wholesale'
                                              ? Number(item.wholesalePrice)
                                              : Number(item.sellingPrice || 0);
                                          const itemVat = toNum(item.vatPercent);
                                          const calculatedUnitPrice =
                                            vatIncluded && itemVat > 0
                                              ? Number((targetBase / (1 + itemVat / 100)).toFixed(company?.currencyDecimals ?? 2))
                                              : targetBase;

                                          setItems((prev) => {
                                            const updated = [...prev];
                                            updated[index] = {
                                              ...updated[index],
                                              unitPrice: calculatedUnitPrice,
                                              inclusivePriceInput: undefined,
                                              pricingType: nextType,
                                            };
                                            return updated;
                                          });
                                        }}
                                        sx={{
                                          cursor: 'pointer',
                                          fontWeight: 700,
                                          fontSize: '0.65rem',
                                          height: 22,
                                          px: 0.25,
                                          borderRadius: 1,
                                          userSelect: 'none',
                                        }}
                                      />
                                    </Tooltip>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={item.discount}
                                  onChange={(e) => handleItemChange(index, 'discount', e.target.value)}
                                  sx={{ width: 65, '& input': { p: '4px 6px' } }}
                                  inputProps={{ min: 0, step: 0.01 }}
                                  disabled={isView}
                                />
                              </TableCell>
                              {isTaxEnabled && (
                                <TableCell>
                                  <TextField
                                    type="number"
                                    size="small"
                                    value={item.vatPercent}
                                    onChange={(e) => {
                                      handleItemChange(index, 'vatPercent', e.target.value);
                                      handleItemChange(index, 'inclusivePriceInput', undefined);
                                    }}
                                    sx={{ width: 50, '& input': { p: '4px 4px' } }}
                                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                                    disabled={isView}
                                  />
                                </TableCell>
                              )}
                              {isTaxEnabled && <TableCell sx={{ fontSize: '0.82rem', fontWeight: 500 }}>{vatAmount.toFixed(dec)}</TableCell>}
                              <TableCell sx={{ fontWeight: 700, color: item.isReturn ? 'error.main' : '#0F172A' }}>
                                {lineTotalMixed.toFixed(dec)}
                              </TableCell>
                              {!isView && (
                                <TableCell align="center">
                                  <IconButton size="small" color="error" onClick={() => handleRemoveItem(index)}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>

              {/* 4. Order Notes Accordion */}
              <Accordion
                elevation={0}
                sx={{
                  border: '1px solid #E2E8F0',
                  borderRadius: '6px !important',
                  '&:before': { display: 'none' },
                  bgcolor: '#FFFFFF',
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#475569' }}>
                    📝 Order Notes &amp; Special Remarks (Optional)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <TextField
                    fullWidth
                    placeholder="Enter order notes, delivery instructions, or customer remarks..."
                    name="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    multiline
                    rows={2}
                    disabled={isView}
                  />
                </AccordionDetails>
              </Accordion>

            </Box>
          </Grid>

          {/* RIGHT PANEL: Sticky Billing Breakdown, Grand Total Hero & Checkout Settlement */}
          <Grid size={{ xs: 12, lg: 4 }}>
            <Box
              sx={{
                position: { lg: 'sticky' },
                top: 16,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              {/* Grand Total Hero Card */}
              <Card
                elevation={0}
                sx={{
                  background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
                  color: '#FFFFFF',
                  borderRadius: 1.25,
                  p: 2.5,
                  boxShadow: '0 4px 12px rgba(15, 23, 42, 0.15)',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        fontWeight: 700,
                        color: '#94A3B8',
                        display: 'block',
                        mb: 0.5,
                      }}
                    >
                      Total Amount Due
                    </Typography>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 800,
                        fontSize: { xs: '2rem', sm: '2.4rem' },
                        letterSpacing: '-0.02em',
                        color: '#38BDF8',
                        lineHeight: 1.1,
                      }}
                    >
                      {currency} {totalAmount.toFixed(dec)}
                    </Typography>
                  </Box>
                  <Chip
                    label={formData.salesType === 'credit' ? 'CREDIT SALE' : 'CASH SALE'}
                    size="small"
                    sx={{
                      bgcolor: formData.salesType === 'credit' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(16, 185, 129, 0.2)',
                      color: formData.salesType === 'credit' ? '#FCA5A5' : '#6EE7B7',
                      fontWeight: 700,
                      fontSize: '0.7rem',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: formData.salesType === 'credit' ? 'rgba(239, 68, 68, 0.3)' : 'rgba(16, 185, 129, 0.3)',
                    }}
                  />
                </Box>

                <Divider sx={{ my: 1.5, borderColor: 'rgba(255, 255, 255, 0.12)' }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                    Items: <strong style={{ color: '#F1F5F9' }}>{items.length}</strong> &bull; Qty: <strong style={{ color: '#F1F5F9' }}>{totalQty}</strong>
                  </Typography>
                  {isTaxEnabled && (
                    <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                      Tax: <strong style={{ color: '#F1F5F9' }}>{currency} {orderVatTotal.toFixed(dec)}</strong>
                    </Typography>
                  )}
                </Box>
              </Card>

              {/* Billing Breakdown Paper */}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 1.25,
                  border: '1px solid #E2E8F0',
                  bgcolor: '#FFFFFF',
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#334155' }}>
                  Billing Breakdown
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Subtotal</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{currency} {orderSubtotal.toFixed(dec)}</Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Bill Discount</Typography>
                    <Box sx={{ maxWidth: 110 }}>
                      <TextField
                        size="small"
                        type="number"
                        value={billDiscount}
                        onChange={(e) => setBillDiscount(e.target.value)}
                        inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right', padding: '3px 8px', fontSize: '0.82rem' } }}
                        disabled={isView}
                      />
                    </Box>
                  </Box>

                  {orderDiscountTotal > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">Total Discount</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
                        -{currency} {orderDiscountTotal.toFixed(dec)}
                      </Typography>
                    </Box>
                  )}

                  {(isTaxEnabled || orderVatTotal > 0) && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">{company?.taxLabel || 'VAT'} Total</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{currency} {orderVatTotal.toFixed(dec)}</Typography>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Round Off</Typography>
                    <Box sx={{ maxWidth: 100 }}>
                      <TextField
                        size="small"
                        type="number"
                        name="roundOff"
                        value={formData.roundOff}
                        onChange={(e) => setFormData({ ...formData, roundOff: e.target.value })}
                        inputProps={{ step: 0.01, style: { textAlign: 'right', padding: '3px 8px', fontSize: '0.82rem' } }}
                        disabled={isView}
                      />
                    </Box>
                  </Box>

                  <Divider sx={{ my: 0.5 }} />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Net Payable</Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'primary.main' }}>
                      {currency} {totalAmount.toFixed(dec)}
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {/* Payment Settlement Paper */}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 1.25,
                  border: '1px solid #E2E8F0',
                  bgcolor: '#FFFFFF',
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#334155' }}>
                  Payment Settlement
                </Typography>

                {/* Quick Payment Method Switchers */}
                {!isView && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.75 }}>
                      Quick Method:
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.75 }}>
                      <Button
                        variant={payments.length === 1 && payments[0].method === 'cash' ? 'contained' : 'outlined'}
                        size="small"
                        onClick={() => handleQuickMethod('cash')}
                        startIcon={<AttachMoneyIcon fontSize="small" />}
                        sx={{ textTransform: 'none', fontWeight: 700, py: 0.6, fontSize: '0.75rem', borderRadius: 1 }}
                      >
                        Cash
                      </Button>
                      <Button
                        variant={payments.length === 1 && payments[0].method === 'card' ? 'contained' : 'outlined'}
                        size="small"
                        onClick={() => handleQuickMethod('card')}
                        startIcon={<CreditCardIcon fontSize="small" />}
                        sx={{ textTransform: 'none', fontWeight: 700, py: 0.6, fontSize: '0.75rem', borderRadius: 1 }}
                      >
                        Card
                      </Button>
                      <Button
                        variant={payments.length === 1 && payments[0].method === 'bank' ? 'contained' : 'outlined'}
                        size="small"
                        onClick={() => handleQuickMethod('bank')}
                        startIcon={<AccountBalanceIcon fontSize="small" />}
                        sx={{ textTransform: 'none', fontWeight: 700, py: 0.6, fontSize: '0.75rem', borderRadius: 1 }}
                      >
                        Bank
                      </Button>
                      <Button
                        variant={payments.length === 1 && payments[0].method === 'advance' ? 'contained' : 'outlined'}
                        size="small"
                        onClick={() => handleQuickMethod('advance')}
                        startIcon={<AccountBalanceWalletIcon fontSize="small" />}
                        disabled={!selectedCustomer || Number(selectedCustomer.advanceBalance || 0) <= 0}
                        sx={{ textTransform: 'none', fontWeight: 700, py: 0.6, fontSize: '0.75rem', borderRadius: 1 }}
                      >
                        Advance
                      </Button>
                    </Box>
                  </Box>
                )}

                {/* Quick Cash Presets */}
                {!isView && (payments.length === 0 || payments.some((p) => p.method === 'cash')) && (
                  <Box sx={{ mb: 2, p: 1.5, bgcolor: '#F8FAFC', borderRadius: 1, border: '1px dashed #CBD5E1' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.75 }}>
                      Quick Cash Tenders:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        onClick={() => handleQuickCashTender(totalAmount)}
                        sx={{ fontWeight: 700, borderRadius: 1, fontSize: '0.72rem', px: 1 }}
                      >
                        Exact Cash
                      </Button>
                      {[50, 100, 200, 500, 1000].map((amt) => (
                        <Button
                          key={amt}
                          size="small"
                          variant="outlined"
                          onClick={() => handleQuickCashTender(amt)}
                          sx={{ fontWeight: 600, borderRadius: 1, fontSize: '0.72rem', minWidth: 44, px: 0.75 }}
                        >
                          {amt}
                        </Button>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Split Payments List */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                  {payments.map((p, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 1.25,
                        borderRadius: 1,
                        bgcolor: '#F8FAFC',
                        border: '1px solid #E2E8F0',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <TextField
                          select
                          size="small"
                          label="Method"
                          value={p.method}
                          onChange={(e) => {
                            const next = [...payments];
                            next[index].method = e.target.value;
                            setPayments(next);
                            setAutoPaymentAmount(false);
                          }}
                          sx={{ minWidth: 90, flex: 1 }}
                          disabled={isView}
                        >
                          <MenuItem value="cash">Cash</MenuItem>
                          <MenuItem value="bank">Bank</MenuItem>
                          <MenuItem value="card">Card</MenuItem>
                          <MenuItem value="cheque">Cheque</MenuItem>
                          <MenuItem value="advance">Advance</MenuItem>
                          <MenuItem value="other">Other</MenuItem>
                        </TextField>

                        <TextField
                          type="number"
                          size="small"
                          label={`Amount (${currency})`}
                          value={p.amount}
                          onChange={(e) => {
                            const next = [...payments];
                            next[index].amount = e.target.value;
                            setPayments(next);
                            setAutoPaymentAmount(false);
                          }}
                          sx={{ width: 120 }}
                          inputProps={{ min: 0, step: 0.01 }}
                          disabled={isView}
                        />

                        {!isView && payments.length > 1 && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setPayments(payments.filter((_, i) => i !== index))}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>

                      {/* Account and Reference */}
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                          select
                          size="small"
                          label="Account"
                          value={p.paymentAccountId || ''}
                          onChange={(e) => {
                            const next = [...payments];
                            next[index].paymentAccountId = e.target.value;
                            setPayments(next);
                            setAutoPaymentAmount(false);
                          }}
                          sx={{ flex: 1 }}
                          disabled={isView}
                        >
                          <MenuItem value="">Auto (Default)</MenuItem>
                          {paymentAccounts?.map((acc) => (
                            <MenuItem key={acc.id} value={acc.id}>
                              {acc.code} - {acc.name}
                            </MenuItem>
                          ))}
                        </TextField>

                        <TextField
                          size="small"
                          label="Ref / Card #"
                          placeholder="Txn ID"
                          value={p.referenceNo || ''}
                          onChange={(e) => {
                            const next = [...payments];
                            next[index].referenceNo = e.target.value;
                            setPayments(next);
                            setAutoPaymentAmount(false);
                          }}
                          sx={{ flex: 1 }}
                          disabled={isView}
                        />
                      </Box>
                    </Box>
                  ))}

                  {!isView && (
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<AddIcon fontSize="small" />}
                      onClick={() => {
                        setPayments((prev) => [
                          ...prev,
                          {
                            method: 'cash',
                            paymentAccountId: '',
                            referenceNo: '',
                            amount: remaining > 0 ? remaining.toFixed(dec) : '0.00',
                          },
                        ]);
                        setAutoPaymentAmount(false);
                      }}
                      sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 600 }}
                    >
                      + Add Split Payment Method
                    </Button>
                  )}
                </Box>

                {/* Paid Total & Change / Remaining Display */}
                <Divider sx={{ my: 1.5 }} />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Total Paid</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {currency} {paidTotal.toFixed(dec)}
                    </Typography>
                  </Box>

                  {change > 0 ? (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        bgcolor: '#ECFDF5',
                        border: '1.5px solid #10B981',
                        borderRadius: 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon sx={{ color: '#059669', fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#065F46' }}>
                          Change to Return
                        </Typography>
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: '#047857' }}>
                        {currency} {change.toFixed(dec)}
                      </Typography>
                    </Paper>
                  ) : remaining > 0 ? (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.25,
                        bgcolor: '#FFFBEB',
                        border: '1px solid #F59E0B',
                        borderRadius: 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#B45309' }}>
                        Remaining Due:
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#D97706' }}>
                        {currency} {remaining.toFixed(dec)}
                      </Typography>
                    </Paper>
                  ) : (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1,
                        bgcolor: '#F0FDF4',
                        border: '1px solid #86EFAC',
                        borderRadius: 1,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <CheckCircleIcon sx={{ color: '#16A34A', fontSize: 16 }} />
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#15803D' }}>
                        Exact Payment Settled
                      </Typography>
                    </Paper>
                  )}
                </Box>
              </Paper>

              {/* High-Speed Action Buttons */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {isView ? (
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={() => navigate('/sales')}
                    sx={{ py: 1.5, borderRadius: 1, fontWeight: 700 }}
                  >
                    Back to Sales
                  </Button>
                ) : (
                  <>
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      size="large"
                      color="primary"
                      disabled={items.length === 0}
                      onClick={() => {
                        submitModeRef.current = 'save-print';
                      }}
                      sx={{
                        py: 1.4,
                        borderRadius: 1,
                        fontWeight: 700,
                        fontSize: '0.98rem',
                        boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
                        textTransform: 'none',
                        display: 'flex',
                        justifyContent: 'space-between',
                        px: 2.5,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PrintIcon />
                        <span>Save &amp; Print Invoice</span>
                      </Box>
                      <Chip
                        label="F4"
                        size="small"
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.25)',
                          color: '#FFFFFF',
                          fontWeight: 800,
                          height: 22,
                          fontSize: '0.72rem',
                          borderRadius: '4px',
                        }}
                      />
                    </Button>

                    <Button
                      type="submit"
                      fullWidth
                      variant="outlined"
                      size="large"
                      color="primary"
                      disabled={items.length === 0}
                      onClick={() => {
                        submitModeRef.current = 'save';
                      }}
                      sx={{
                        py: 1.1,
                        borderRadius: 1,
                        fontWeight: 700,
                        textTransform: 'none',
                        display: 'flex',
                        justifyContent: 'space-between',
                        px: 2.5,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SaveIcon fontSize="small" />
                        <span>Create Order Only</span>
                      </Box>
                      <Chip
                        label="F2"
                        size="small"
                        variant="outlined"
                        color="primary"
                        sx={{ fontWeight: 800, height: 22, fontSize: '0.72rem', borderRadius: '4px' }}
                      />
                    </Button>

                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                      <Button
                        type="submit"
                        variant="outlined"
                        color="warning"
                        disabled={items.length === 0}
                        onClick={() => {
                          submitModeRef.current = 'save-hold';
                        }}
                        startIcon={<PauseCircleOutlineIcon fontSize="small" />}
                        sx={{
                          py: 1,
                          borderRadius: 1,
                          fontWeight: 600,
                          fontSize: '0.82rem',
                          textTransform: 'none',
                        }}
                      >
                        Hold Bill [F3]
                      </Button>

                      <Button
                        type="button"
                        variant="outlined"
                        color="error"
                        onClick={() => setCancelDialogOpen(true)}
                        startIcon={<CloseIcon fontSize="small" />}
                        sx={{
                          py: 1,
                          borderRadius: 1,
                          fontWeight: 600,
                          fontSize: '0.82rem',
                          textTransform: 'none',
                        }}
                      >
                        Cancel [F6]
                      </Button>
                    </Box>
                  </>
                )}
              </Box>

            </Box>
          </Grid>
        </Grid>
      </form>
      <Dialog
        open={customerDialogOpen}
        onClose={() => setCustomerDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 1.5, boxShadow: '0 12px 40px rgba(0,0,0,0.15)' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Add Customer</DialogTitle>
        <DialogContent
          sx={{
            pt: 2,
            overflow: 'visible',
            '& .MuiInputLabel-root': { overflow: 'visible' },
          }}
        >
          <CustomerFormContent
            compact
            onCancel={() => setCustomerDialogOpen(false)}
            onSuccess={(created) => {
              setCustomerDialogOpen(false);
              if (created?.id) {
                setCustomerSearchOptions(prev => [created, ...prev]);
                setFormData((prev) => ({ ...formData, customerId: created.id }));
              }
            }}
          />
        </DialogContent>
      </Dialog>
      <Dialog
        open={receivePaymentOpen}
        onClose={() => setReceivePaymentOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 1.5 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Receive Payment</DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            type="date"
            label="Payment Date *"
            value={receivePaymentForm.paymentDate}
            onChange={(e) =>
              setReceivePaymentForm((prev) => ({ ...prev, paymentDate: e.target.value }))
            }
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            select
            label="Method *"
            value={receivePaymentForm.paymentMethod}
            onChange={(e) =>
              setReceivePaymentForm((prev) => ({ ...prev, paymentMethod: e.target.value }))
            }
            fullWidth
          >
            <MenuItem value="cash">Cash</MenuItem>
            <MenuItem value="bank">Bank</MenuItem>
            <MenuItem value="card">Card</MenuItem>
            <MenuItem value="cheque">Cheque</MenuItem>
          </TextField>
          <TextField
            select
            label="Account (optional)"
            value={receivePaymentForm.paymentAccountId}
            onChange={(e) =>
              setReceivePaymentForm((prev) => ({
                ...prev,
                paymentAccountId: e.target.value,
              }))
            }
            fullWidth
          >
            <MenuItem value="">Auto (by method)</MenuItem>
            {paymentAccounts?.map((acc) => (
              <MenuItem key={acc.id} value={acc.id}>
                {acc.code} - {acc.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            type="number"
            label={`Amount (${currency}) *`}
            value={receivePaymentForm.amount}
            onChange={(e) =>
              setReceivePaymentForm((prev) => ({ ...prev, amount: e.target.value }))
            }
            fullWidth
            inputProps={{ min: 0, step: 0.01 }}
          />
          <TextField
            label="Reference"
            value={receivePaymentForm.referenceNo}
            onChange={(e) =>
              setReceivePaymentForm((prev) => ({ ...prev, referenceNo: e.target.value }))
            }
            fullWidth
          />
          <TextField
            label="Notes"
            value={receivePaymentForm.notes}
            onChange={(e) =>
              setReceivePaymentForm((prev) => ({ ...prev, notes: e.target.value }))
            }
            fullWidth
            multiline
            rows={2}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1, pb: 1 }}>
            <Button onClick={() => setReceivePaymentOpen(false)} disabled={recordingPayment}>
              Close
            </Button>
            <Button
              variant="contained"
              disabled={recordingPayment}
              onClick={async () => {
                if (!existingOrder?.id) return;
                const amt = Number(receivePaymentForm.amount || 0);
                if (!amt || amt <= 0) {
                  alert('Amount must be greater than zero');
                  return;
                }
                try {
                  await recordSalesPayment({
                    id: existingOrder.id,
                    paymentDate: receivePaymentForm.paymentDate,
                    amount: amt,
                    paymentMethod: receivePaymentForm.paymentMethod,
                    paymentAccountId:
                      receivePaymentForm.paymentAccountId || undefined,
                    referenceNo: receivePaymentForm.referenceNo || undefined,
                    notes: receivePaymentForm.notes || undefined,
                  }).unwrap();
                  setReceivePaymentOpen(false);
                } catch (err) {
                  alert(err?.data?.message || 'Error recording payment');
                }
              }}
            >
              {recordingPayment ? 'Saving...' : 'Save Payment'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog open={scanOpen} onClose={() => setScanOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Scan Item Barcode</DialogTitle>
        <DialogContent>
          {scanError ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {scanError}
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              Point the camera at the barcode. The item will be added automatically when matched.
            </Alert>
          )}

          {scanOpen && (
            <BarcodeScannerComponent
              width="100%"
              height={360}
              delay={200}
              constraints={{ facingMode: 'environment' }}
              onUpdate={(err, result) => {
                if (err) {
                  if (err?.name === 'NotAllowedError') {
                    setScanError('Camera permission denied. Please allow camera access and try again.');
                  } else if (err?.name !== 'NotFoundException') {
                    setScanError('Unable to read barcode. Try better lighting or move closer.');
                  }
                  return;
                }
                const text = result?.text ? String(result.text).trim() : '';
                if (!text) return;

                setProductSearch(text);
                setPendingBarcode(text);
                triggerProductSearch({ search: text, limit: 20 });
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setScanOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Cancel Entry?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel? Any unsaved changes will be lost.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setCancelDialogOpen(false)}>
            No
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              setCancelDialogOpen(false);
              window.history.back();
              setTimeout(() => navigate('/sales'), 100);
            }}
          >
            Yes, Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={duplicateDialog.open}
        onClose={() => setDuplicateDialog({ open: false, existingIndex: -1, product: null })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Item already added</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1.5 }}>
            {duplicateDialog.product
              ? `“${formatItemDisplayName(
                {
                  name: duplicateDialog.product.name,
                  sku: duplicateDialog.product.sku || duplicateDialog.product.barcode,
                  brand: duplicateDialog.product.brand,
                },
                itemNameFormat
              )}” is already in the list.`
              : 'This item is already in the list.'}
          </Typography>
          <Typography color="text.secondary">
            Do you want to increase the quantity by 1, or add it as a new line?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setDuplicateDialog({ open: false, existingIndex: -1, product: null })}
          >
            No
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => {
              const product = duplicateDialog.product;
              setDuplicateDialog({ open: false, existingIndex: -1, product: null });
              setProductSearch('');
              setPendingBarcode('');
              setScanOpen(false);
              if (product) {
                handleAddProductRow(product, true);
              }
            }}
          >
            Add as new
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const idx = duplicateDialog.existingIndex;
              setItems((prev) => {
                if (idx < 0 || idx >= prev.length) return prev;
                const next = [...prev];
                const currentQty = Number(next[idx]?.quantity || 0) || 0;
                next[idx] = {
                  ...next[idx],
                  quantity: String(Math.max(0.01, currentQty + 1)),
                };
                return next;
              });
              setDuplicateDialog({ open: false, existingIndex: -1, product: null });
              setProductSearch('');
              setPendingBarcode('');
              setScanOpen(false);
            }}
          >
            Yes, increase
          </Button>
        </DialogActions>
      </Dialog>
      {/* Email Dialog */}
      <Dialog open={emailDialogOpen} onClose={() => !emailLoading && setEmailDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 600 }}>Send Invoice via Email</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Recipient Email"
                value={emailRecipient}
                onChange={(e) => setEmailRecipient(e.target.value)}
                placeholder="customer@example.com"
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                select
                label="Select Template"
                value={selectedEmailTemplate}
                onChange={(e) => setSelectedEmailTemplate(e.target.value)}
                helperText="Leave empty to use default template"
              >
                <MenuItem value=""><em>Default Template</em></MenuItem>
                {Array.isArray(emailTemplates) &&
                  emailTemplates
                    .filter(t => t.type === 'sales_invoice' || (existingOrder?.status === 'quotation' && t.type === 'quotation'))
                    .map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.name}
                      </MenuItem>
                    ))
                }
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialogOpen(false)} disabled={emailLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSendEmail}
            disabled={emailLoading || !emailRecipient}
            startIcon={emailLoading ? <CircularProgress size={20} /> : <EmailIcon />}
          >
            {emailLoading ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* WhatsApp Dialog */}
      <Dialog
        open={whatsappDialogOpen}
        onClose={() => !whatsappLoading && setWhatsappDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <WhatsAppIcon sx={{ color: '#25D366' }} />
          Share via WhatsApp
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Send the invoice PDF directly to the customer's WhatsApp.
          </Typography>
          <TextField
            fullWidth
            label="WhatsApp Number (with country code)"
            placeholder="e.g. 971501234567"
            value={whatsappPhone}
            onChange={(e) => setWhatsappPhone(e.target.value)}
            disabled={whatsappLoading}
            sx={{ mb: 1.5 }}
            helperText="Include country code without + or 00 prefix."
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={whatsappLoading} onClick={() => setWhatsappDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            disabled={whatsappLoading || !whatsappPhone}
            variant="contained"
            onClick={handleSendWhatsapp}
            sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#128C7E' }, minWidth: 140 }}
            startIcon={whatsappLoading ? <CircularProgress size={20} /> : <WhatsAppIcon />}
          >
            {whatsappLoading ? 'Processing...' : 'Send WhatsApp'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Open Register Dialog */}
      <Dialog
        open={openRegisterDialogOpen}
        onClose={() => registerOpen && setOpenRegisterDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Open Cash Register Session</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Please open your register session by entering the starting cash balance.
          </Typography>
          <TextField
            fullWidth
            type="number"
            label="Opening Cash Balance"
            placeholder="e.g. 100.00"
            value={openingBalanceInput}
            onChange={(e) => setOpeningBalanceInput(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          {registerOpen && (
            <Button onClick={() => setOpenRegisterDialogOpen(false)}>
              Cancel
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleOpenRegister}
            disabled={!openingBalanceInput}
            sx={{ fontWeight: 600 }}
          >
            Open Register
          </Button>
        </DialogActions>
      </Dialog>

      {/* Close Register Dialog */}
      <Dialog
        open={closeRegisterDialogOpen}
        onClose={() => setCloseRegisterDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StorefrontIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Close Register & Shift Summary</Typography>
          </Box>
          <Chip
            size="small"
            label="Active Shift"
            color="success"
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2.5 }}>
          {(() => {
            const fin = registerSession?.financials;
            const opening = Number(fin?.openingBalance ?? registerSession?.openingBalance ?? 0);
            const totalSales = Number(fin?.totalSales ?? registerSession?.salesTotal ?? 0);
            const salesCount = fin?.salesCount ?? registerSession?.salesCount ?? 0;
            const cashSales = Number(fin?.cashSales ?? 0);
            const cardSales = Number(fin?.cardSales ?? 0);
            const creditSales = Number(fin?.creditSales ?? 0);
            const bankSales = Number(fin?.bankSales ?? 0);
            const chequeSales = Number(fin?.chequeSales ?? 0);

            const creditReceivedCash = Number(fin?.customerCreditCollectedCash ?? 0);
            const creditReceivedCard = Number(fin?.customerCreditCollectedCard ?? 0);
            const creditReceivedBank = Number(fin?.customerCreditCollectedBank ?? 0);

            const cashPurchases = Number(fin?.cashPurchases ?? 0);
            const cashExpenses = Number(fin?.cashExpenses ?? 0);
            const supplierPaymentsCash = Number(fin?.supplierPaymentsCash ?? 0);
            const cashRefunds = Number(fin?.cashRefunds ?? 0);
            const tradeInPayoutCash = Number(fin?.tradeInCashPayout ?? 0);
            const cashToBank = Number(fin?.cashToBankTransfers ?? 0);
            const bankToCash = Number(fin?.bankToCashTransfers ?? 0);

            const totalCashIn = Number(fin?.totalCashIn ?? (opening + cashSales + creditReceivedCash + bankToCash));
            const totalCashOut = Number(fin?.totalCashOut ?? (cashPurchases + cashExpenses + supplierPaymentsCash + cashRefunds + tradeInPayoutCash + cashToBank));
            const expected = Number(fin?.expectedClosingCash ?? (totalCashIn - totalCashOut));

            const actual = parseFloat(closingBalanceInput) || 0;
            const petty = parseFloat(closingPettyCashInput) || 0;
            const diff = closingBalanceInput !== '' ? actual - expected : 0;
            const netTransfer = Math.max(0, actual - petty);

            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Shift Info Banner */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'grey.50', p: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Shift Opened</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {registerSession?.openedAt ? formatDisplayDateTime(registerSession.openedAt, company?.dateFormat, company?.timezone) : '–'}
                      {registerSession?.user?.firstName ? ` (by ${registerSession.user.firstName})` : ''}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" color="text.secondary">Opening Cash Float</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      {opening.toFixed(2)} {currency}
                    </Typography>
                  </Box>
                </Box>

                {/* Section 1: Sales Summary Breakdown */}
                <Card variant="outlined" sx={{ borderRadius: 1.5 }}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ReceiptLongIcon fontSize="small" color="action" />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          Shift Sales Summary ({salesCount} {salesCount === 1 ? 'Order' : 'Orders'})
                        </Typography>
                      </Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        {totalSales.toFixed(2)} {currency}
                      </Typography>
                    </Box>

                    <Grid container spacing={1}>
                      <Grid size={{ xs: 6 }}>
                        <Box sx={{ p: 1, bgcolor: '#f0fdf4', borderRadius: 1, border: '1px solid #bbf7d0' }}>
                          <Typography variant="caption" color="text.secondary">
                            💵 Cash Sales (In Drawer)
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#166534' }}>
                            {cashSales.toFixed(2)} {currency}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Box sx={{ p: 1, bgcolor: '#eff6ff', borderRadius: 1, border: '1px solid #bfdbfe' }}>
                          <Typography variant="caption" color="text.secondary">
                            💳 Card / POS (To Bank)
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e40af' }}>
                            {cardSales.toFixed(2)} {currency}
                          </Typography>
                        </Box>
                      </Grid>
                      {creditSales > 0 && (
                        <Grid size={{ xs: 6 }}>
                          <Box sx={{ p: 1, bgcolor: '#fffbeb', borderRadius: 1, border: '1px solid #fde68a' }}>
                            <Typography variant="caption" color="text.secondary">
                              📑 Credit Sales (Receivable)
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#92400e' }}>
                              {creditSales.toFixed(2)} {currency}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                      {(bankSales > 0 || chequeSales > 0) && (
                        <Grid size={{ xs: 6 }}>
                          <Box sx={{ p: 1, bgcolor: 'grey.100', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="caption" color="text.secondary">
                              🏦 Bank / Cheque Sales
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {(bankSales + chequeSales).toFixed(2)} {currency}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>

                {/* Section 2: Other Cash Movements */}
                {(creditReceivedCash > 0 || creditReceivedCard > 0 || cashPurchases > 0 || cashExpenses > 0 || supplierPaymentsCash > 0 || cashRefunds > 0 || tradeInPayoutCash > 0 || cashToBank > 0 || bankToCash > 0) && (
                  <Card variant="outlined" sx={{ borderRadius: 1.5 }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccountBalanceWalletIcon fontSize="small" color="action" />
                        Other Shift Cash Movements
                      </Typography>
                      <Grid container spacing={1}>
                        {bankToCash > 0 && (
                          <Grid size={{ xs: 6 }}>
                            <Box sx={{ p: 1, bgcolor: '#f0fdf4', borderRadius: 1, border: '1px solid #bbf7d0' }}>
                              <Typography variant="caption" sx={{ color: '#166534', fontWeight: 500 }}>
                                (+) Bank to Cash (Withdrawal)
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#166534' }}>
                                +{bankToCash.toFixed(2)} {currency}
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                        {creditReceivedCash > 0 && (
                          <Grid size={{ xs: 6 }}>
                            <Box sx={{ p: 1, bgcolor: '#f0fdf4', borderRadius: 1, border: '1px solid #bbf7d0' }}>
                              <Typography variant="caption" sx={{ color: '#166534', fontWeight: 500 }}>
                                (+) Credit Received (Cash)
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#166534' }}>
                                +{creditReceivedCash.toFixed(2)} {currency}
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                        {cashToBank > 0 && (
                          <Grid size={{ xs: 6 }}>
                            <Box sx={{ p: 1, bgcolor: '#fef2f2', borderRadius: 1, border: '1px solid #fecaca' }}>
                              <Typography variant="caption" sx={{ color: '#991b1b', fontWeight: 500 }}>
                                (−) Cash to Bank (Drop / Deposit)
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#991b1b' }}>
                                -{cashToBank.toFixed(2)} {currency}
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                        {cashPurchases > 0 && (
                          <Grid size={{ xs: 6 }}>
                            <Box sx={{ p: 1, bgcolor: '#fef2f2', borderRadius: 1, border: '1px solid #fecaca' }}>
                              <Typography variant="caption" sx={{ color: '#991b1b', fontWeight: 500 }}>
                                (−) Cash Purchases
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#991b1b' }}>
                                -{cashPurchases.toFixed(2)} {currency}
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                        {cashExpenses > 0 && (
                          <Grid size={{ xs: 6 }}>
                            <Box sx={{ p: 1, bgcolor: '#fef2f2', borderRadius: 1, border: '1px solid #fecaca' }}>
                              <Typography variant="caption" sx={{ color: '#991b1b', fontWeight: 500 }}>
                                (−) Cash Expenses
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#991b1b' }}>
                                -{cashExpenses.toFixed(2)} {currency}
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                        {supplierPaymentsCash > 0 && (
                          <Grid size={{ xs: 6 }}>
                            <Box sx={{ p: 1, bgcolor: '#fef2f2', borderRadius: 1, border: '1px solid #fecaca' }}>
                              <Typography variant="caption" sx={{ color: '#991b1b', fontWeight: 500 }}>
                                (−) Supplier Debt Paid (Cash)
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#991b1b' }}>
                                -{supplierPaymentsCash.toFixed(2)} {currency}
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                        {cashRefunds > 0 && (
                          <Grid size={{ xs: 6 }}>
                            <Box sx={{ p: 1, bgcolor: '#fef2f2', borderRadius: 1, border: '1px solid #fecaca' }}>
                              <Typography variant="caption" sx={{ color: '#991b1b', fontWeight: 500 }}>
                                (−) Cash Refunds
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#991b1b' }}>
                                -{cashRefunds.toFixed(2)} {currency}
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                        {tradeInPayoutCash > 0 && (
                          <Grid size={{ xs: 6 }}>
                            <Box sx={{ p: 1, bgcolor: '#fef2f2', borderRadius: 1, border: '1px solid #fecaca' }}>
                              <Typography variant="caption" sx={{ color: '#991b1b', fontWeight: 500 }}>
                                (−) Trade-In Payout (Cash)
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#991b1b' }}>
                                -{tradeInPayoutCash.toFixed(2)} {currency}
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>
                )}

                {/* Section 3: Drawer Cash Reconciliation Box */}
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.15)' : '#eef7fe',
                    border: '1.5px solid',
                    borderColor: 'primary.light',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'primary.main' }}>
                        Expected Physical Cash in Drawer
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Opening ({opening.toFixed(2)}) + Cash In (+{(cashSales + creditReceivedCash).toFixed(2)}) − Cash Out (-{totalCashOut.toFixed(2)})
                      </Typography>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: 'primary.dark' }}>
                      {expected.toFixed(2)} {currency}
                    </Typography>
                  </Box>
                </Box>

                {/* Section 4: Cashier Inputs */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Actual Closing Cash Counted in Drawer"
                    placeholder="Enter physical cash counted"
                    value={closingBalanceInput}
                    onChange={(e) => setClosingBalanceInput(e.target.value)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">💵</InputAdornment>,
                      endAdornment: <InputAdornment position="end">{currency}</InputAdornment>,
                    }}
                    autoFocus
                  />
                  <TextField
                    fullWidth
                    type="number"
                    label="Petty Cash to Keep in Drawer for Next Shift"
                    placeholder="e.g. 100.00"
                    value={closingPettyCashInput}
                    onChange={(e) => setClosingPettyCashInput(e.target.value)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">🪙</InputAdornment>,
                      endAdornment: <InputAdornment position="end">{currency}</InputAdornment>,
                    }}
                  />
                </Box>

                {/* Section 5: Live Discrepancy & Safe Transfer Alerts */}
                {closingBalanceInput !== '' && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Alert
                      severity={Math.abs(diff) < 0.01 ? 'success' : diff > 0 ? 'warning' : 'error'}
                      sx={{ fontWeight: 600 }}
                    >
                      Discrepancy: {diff >= 0 ? '+' : ''}{diff.toFixed(2)} {currency}
                      {Math.abs(diff) < 0.01
                        ? ' — Perfectly Reconciled! ✅'
                        : diff > 0
                        ? ' — Cash Surplus (Extra cash in drawer)'
                        : ' — Cash Shortage (Missing cash in drawer)'}
                    </Alert>
                    <Alert severity="info" sx={{ fontWeight: 500 }}>
                      Net Cash Transfer to Safe: <strong>{netTransfer.toFixed(2)} {currency}</strong>
                      {petty > 0 && ` (leaving ${petty.toFixed(2)} ${currency} float in drawer)`}
                    </Alert>
                  </Box>
                )}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCloseRegisterDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleCloseRegister}
            disabled={!closingBalanceInput}
            sx={{ fontWeight: 700, px: 3 }}
          >
            Confirm & Close Shift
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={activeSerialIndex !== null}
        onClose={() => setActiveSerialIndex(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Enter Serial Numbers</DialogTitle>
        <DialogContent>
          {activeSerialIndex !== null && items[activeSerialIndex] && (() => {
            const item = items[activeSerialIndex];
            const qty = Math.max(1, Math.floor(Number(item.quantity || 1)));
            const currentSerials = Array.isArray(item.serialNumbers) ? [...item.serialNumbers] : [];
            while (currentSerials.length < qty) currentSerials.push('');
            if (currentSerials.length > qty) currentSerials.length = qty;

            if (activeSerialScanIndex !== null) {
              return (
                <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Scanning Serial Number {activeSerialScanIndex + 1}
                  </Typography>
                  <BarcodeScannerComponent
                    width="100%"
                    height={300}
                    delay={200}
                    constraints={{ facingMode: 'environment' }}
                    onUpdate={(err, result) => {
                      if (err) return;
                      const text = result?.text ? String(result.text).trim() : '';
                      if (!text) return;

                      const newSerials = [...currentSerials];
                      newSerials[activeSerialScanIndex] = text;
                      handleItemChange(activeSerialIndex, 'serialNumbers', newSerials);
                      
                      const nextIndex = newSerials.findIndex((s, i) => i > activeSerialScanIndex && !s);
                      if (nextIndex !== -1) {
                         setActiveSerialScanIndex(nextIndex);
                      } else {
                         setActiveSerialScanIndex(null);
                      }
                    }}
                  />
                  <Button variant="outlined" onClick={() => setActiveSerialScanIndex(null)}>Cancel Scan</Button>
                </Box>
              );
            }

            return (
              <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Enter {qty} unique serial numbers for {item.productName}. You can type or scan them.
                </Typography>
                {currentSerials.map((serial, idx) => (
                  <TextField
                    key={idx}
                    label={`Serial Number ${idx + 1}`}
                    value={serial}
                    size="small"
                    fullWidth
                    onChange={(e) => {
                      const newSerials = [...currentSerials];
                      newSerials[idx] = e.target.value;
                      handleItemChange(activeSerialIndex, 'serialNumbers', newSerials);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const nextInput = document.getElementById(`serial-input-${idx + 1}`);
                        if (nextInput) nextInput.focus();
                      }
                    }}
                    id={`serial-input-${idx}`}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="Scan serial"
                            edge="end"
                            onClick={() => setActiveSerialScanIndex(idx)}
                          >
                            <QrCodeScannerIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                ))}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActiveSerialIndex(null)} variant="contained">Done</Button>
        </DialogActions>
      </Dialog>
      
      {/* IMEI Modal */}
      <Dialog open={imeiModal.open} onClose={() => setImeiModal({ open: false, itemIndex: -1, imeis: [], text: '' })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Scan / Enter IMEIs</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, mt: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Please enter one IMEI per line. You can also use a barcode scanner to scan them sequentially.
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={6}
              label="IMEIs / Serial Numbers"
              placeholder="e.g. 123456789012345&#10;123456789012346"
              value={imeiModal.text}
              onChange={(e) => {
                const val = e.target.value;
                const arr = val.split(/[\n,]+/).map(i => i.trim()).filter(i => i);
                setImeiModal(prev => ({ ...prev, text: val, imeis: arr }));
              }}
            />
            <Typography variant="caption" sx={{ mt: 1, display: 'block' }} color={imeiModal.itemIndex >= 0 && imeiModal.imeis.length === (parseFloat(items[imeiModal.itemIndex]?.quantity || 0) * (items[imeiModal.itemIndex]?.requiresDualImei ? 2 : 1)) ? 'success.main' : 'error.main'}>
              {imeiModal.imeis.length} of {imeiModal.itemIndex >= 0 ? parseFloat(items[imeiModal.itemIndex]?.quantity || 0) * (items[imeiModal.itemIndex]?.requiresDualImei ? 2 : 1) : 0} required IMEIs provided
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImeiModal({ open: false, itemIndex: -1, imeis: [], text: '' })}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              const reqQty = (parseFloat(items[imeiModal.itemIndex]?.quantity) || 0) * (items[imeiModal.itemIndex]?.requiresDualImei ? 2 : 1);
              if (imeiModal.imeis.length !== reqQty) {
                 alert(`Please provide exactly ${reqQty} IMEIs.`);
                 return;
              }
              handleItemChange(imeiModal.itemIndex, 'imeis', imeiModal.imeis);
              setImeiModal({ open: false, itemIndex: -1, imeis: [], text: '' });
            }}
          >
            Save IMEIs
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={productDialogOpen}
        onClose={() => setProductDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 1.5, boxShadow: '0 12px 40px rgba(0,0,0,0.15)' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Add Product</DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 0, overflow: 'visible', '& .MuiInputLabel-root': { overflow: 'visible' } }}>
          <ProductFormContent
            compact
            onCancel={() => setProductDialogOpen(false)}
            onSuccess={(created) => {
              setProductDialogOpen(false);
              if (created?.id) {
                // optionally auto-add the created product to order items
                handleAddProductRow(created);
              }
            }}
          />
        </DialogContent>
      </Dialog>
      
      <Dialog
        open={historyModal.open}
        onClose={() => setHistoryModal({ ...historyModal, open: false })}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 1.5, boxShadow: '0 12px 40px rgba(0,0,0,0.15)' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          Sales Price History - {historyModal.productName}
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 3 }}>
          {fetchingHistory ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : !historyData || historyData.length === 0 ? (
            <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
              No past sales found for this customer and product.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Invoice #</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Qty</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Unit Price</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyData.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{new Date(row.invoiceDate).toLocaleDateString()}</TableCell>
                      <TableCell>{row.invoiceNumber}</TableCell>
                      <TableCell align="center">{row.quantity}</TableCell>
                      <TableCell align="right">{company?.currency} {Number(row.unitPrice).toFixed(company?.currencyDecimals || 2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryModal({ ...historyModal, open: false })}>Close</Button>
        </DialogActions>
      </Dialog>
      
      {/* Item Cost & Margin Modal (F7) */}
      <Dialog
        open={costModal.open}
        onClose={() => setCostModal({ ...costModal, open: false })}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 1.25 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ bgcolor: 'primary.light', color: 'primary.contrastText', p: 1, borderRadius: 1, display: 'flex' }}>
              <CalculateIcon />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                Item Cost & Profit Breakdown
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Shortcut: <strong>F7</strong> • Confidential Internal Cost Analysis
              </Typography>
            </Box>
          </Box>
          <Chip label="F7 Shortcut" size="small" color="primary" variant="outlined" sx={{ fontWeight: 600, borderRadius: 1 }} />
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2.5 }}>
          {items.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 1 }}>
              No items added to the sales order yet. Add items to view cost and margin breakdown.
            </Alert>
          ) : (
            (() => {
              const activeIndex = (costModal.selectedIndex != null && items[costModal.selectedIndex]) ? costModal.selectedIndex : 0;
              const activeItem = items[activeIndex] || items[0];
              const costPrice = Number(activeItem?.costPrice || 0);
              const unitPrice = Number(activeItem?.unitPrice || 0);
              const qty = Number(activeItem?.quantity || 1);
              const costCode = getCostCode(costPrice);
              const unitProfit = unitPrice - costPrice;
              const marginPercent = unitPrice > 0 ? (((unitPrice - costPrice) / unitPrice) * 100).toFixed(1) : '0.0';
              const lineCost = costPrice * qty;
              const lineSelling = unitPrice * qty;
              const lineProfit = lineSelling - lineCost;

              // Order totals
              const orderTotalCost = items.reduce((sum, it) => sum + (Number(it.costPrice || 0) * Number(it.quantity || 0)), 0);
              const orderTotalSelling = items.reduce((sum, it) => sum + (Number(it.unitPrice || 0) * Number(it.quantity || 0)), 0);
              const orderTotalProfit = orderTotalSelling - orderTotalCost;
              const orderMarginPercent = orderTotalSelling > 0 ? ((orderTotalProfit / orderTotalSelling) * 100).toFixed(1) : '0.0';
              const dec = company?.currencyDecimals ?? 2;

              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  {/* Selected Item Spotlight Card */}
                  <Card sx={{ bgcolor: 'grey.50', borderRadius: 1.25, border: '1px solid', borderColor: 'divider' }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                            {activeItem?.productName || 'Selected Item'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            SKU: <strong>{activeItem?.productSku || 'N/A'}</strong>
                            {activeItem?.productBarcode && ` • Barcode: ${activeItem.productBarcode}`}
                            {activeItem?.productBrand && ` • Brand: ${activeItem.productBrand}`}
                          </Typography>
                        </Box>
                        {costCode && (
                          <Chip
                            label={`Cost Code: ${costCode}`}
                            color="secondary"
                            size="medium"
                            sx={{ fontWeight: 700, letterSpacing: 1, borderRadius: 1 }}
                          />
                        )}
                      </Box>

                      <Grid container spacing={2}>
                        <Grid size={{ xs: 6, sm: 3 }}>
                          <Paper sx={{ p: 1.5, borderRadius: 1, bgcolor: 'background.paper', border: '1px solid', borderColor: 'grey.200' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
                              UNIT COST
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: 'error.main' }}>
                              {company?.currency} {costPrice.toFixed(dec)}
                            </Typography>
                          </Paper>
                        </Grid>

                        <Grid size={{ xs: 6, sm: 3 }}>
                          <Paper sx={{ p: 1.5, borderRadius: 1, bgcolor: 'background.paper', border: '1px solid', borderColor: 'grey.200' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
                              SELLING PRICE
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                              {company?.currency} {unitPrice.toFixed(dec)}
                            </Typography>
                          </Paper>
                        </Grid>

                        <Grid size={{ xs: 6, sm: 3 }}>
                          <Paper sx={{ p: 1.5, borderRadius: 1, bgcolor: 'background.paper', border: '1px solid', borderColor: 'grey.200' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
                              UNIT PROFIT
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: unitProfit >= 0 ? 'success.main' : 'error.main' }}>
                              {company?.currency} {unitProfit.toFixed(dec)}
                            </Typography>
                          </Paper>
                        </Grid>

                        <Grid size={{ xs: 6, sm: 3 }}>
                          <Paper sx={{ p: 1.5, borderRadius: 1, bgcolor: 'background.paper', border: '1px solid', borderColor: 'grey.200' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
                              PROFIT MARGIN
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: Number(marginPercent) >= 0 ? 'success.main' : 'error.main' }}>
                              {marginPercent}%
                            </Typography>
                          </Paper>
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', bgcolor: 'background.paper', p: 1.5, borderRadius: 1, border: '1px dashed', borderColor: 'primary.300', flexWrap: 'wrap', gap: 1 }}>
                            <Typography variant="body2">
                              Line Total (Qty: <strong>{qty}</strong>): Cost = <strong>{company?.currency} {lineCost.toFixed(dec)}</strong> | Sales = <strong>{company?.currency} {lineSelling.toFixed(dec)}</strong>
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: lineProfit >= 0 ? 'success.dark' : 'error.dark' }}>
                              Line Profit: {company?.currency} {lineProfit.toFixed(dec)}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>

                  {/* All Items Table */}
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        All Items in Order ({items.length})
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Click any row to inspect its cost & margin
                      </Typography>
                    </Box>
                    <TableContainer component={Paper} sx={{ maxHeight: 220, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'grey.100' }}>
                            <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Product Name</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 600 }}>Qty</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Unit Cost</TableCell>
                            {company?.costPriceCodeMapping && <TableCell align="center" sx={{ fontWeight: 600 }}>Cost Code</TableCell>}
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Unit Price</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Line Cost</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Line Profit</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 600 }}>Margin %</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {items.map((rowItem, idx) => {
                            const rCost = Number(rowItem.costPrice || 0);
                            const rPrice = Number(rowItem.unitPrice || 0);
                            const rQty = Number(rowItem.quantity || 1);
                            const rCode = getCostCode(rCost);
                            const rLineCost = rCost * rQty;
                            const rLineProfit = (rPrice - rCost) * rQty;
                            const rMargin = rPrice > 0 ? (((rPrice - rCost) / rPrice) * 100).toFixed(1) : '0.0';
                            const isSelected = activeIndex === idx;

                            return (
                              <TableRow
                                key={idx}
                                hover
                                selected={isSelected}
                                onClick={() => setCostModal({ ...costModal, selectedIndex: idx })}
                                sx={{ cursor: 'pointer', bgcolor: isSelected ? 'action.selected' : 'inherit' }}
                              >
                                <TableCell>{idx + 1}</TableCell>
                                <TableCell sx={{ fontWeight: isSelected ? 700 : 500 }}>{rowItem.productName}</TableCell>
                                <TableCell align="center">{rQty}</TableCell>
                                <TableCell align="right" sx={{ color: 'error.main', fontWeight: 600 }}>
                                  {company?.currency} {rCost.toFixed(dec)}
                                </TableCell>
                                {company?.costPriceCodeMapping && (
                                  <TableCell align="center">
                                    <Chip label={rCode || '—'} size="small" sx={{ fontWeight: 600, height: 20, borderRadius: 1 }} />
                                  </TableCell>
                                )}
                                <TableCell align="right">{company?.currency} {rPrice.toFixed(dec)}</TableCell>
                                <TableCell align="right">{company?.currency} {rLineCost.toFixed(dec)}</TableCell>
                                <TableCell align="right" sx={{ color: rLineProfit >= 0 ? 'success.main' : 'error.main', fontWeight: 600 }}>
                                  {company?.currency} {rLineProfit.toFixed(dec)}
                                </TableCell>
                                <TableCell align="center">
                                  <Chip
                                    label={`${rMargin}%`}
                                    size="small"
                                    color={Number(rMargin) >= 20 ? 'success' : Number(rMargin) >= 0 ? 'warning' : 'error'}
                                    variant="outlined"
                                    sx={{ fontWeight: 600, height: 22, borderRadius: 1 }}
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>

                  {/* Order Total Summary Bar */}
                  <Paper sx={{ p: 2, borderRadius: 1.25, bgcolor: 'grey.900', color: 'common.white' }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" sx={{ color: 'grey.400', display: 'block' }}>TOTAL ORDER COST</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'error.light' }}>
                          {company?.currency} {orderTotalCost.toFixed(dec)}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" sx={{ color: 'grey.400', display: 'block' }}>TOTAL SELLING AMOUNT</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.light' }}>
                          {company?.currency} {orderTotalSelling.toFixed(dec)}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" sx={{ color: 'grey.400', display: 'block' }}>ESTIMATED GROSS PROFIT</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: orderTotalProfit >= 0 ? 'success.light' : 'error.light' }}>
                          {company?.currency} {orderTotalProfit.toFixed(dec)}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" sx={{ color: 'grey.400', display: 'block' }}>OVERALL MARGIN</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: Number(orderMarginPercent) >= 0 ? 'success.light' : 'error.light' }}>
                          {orderMarginPercent}%
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Box>
              );
            })()
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2.5, py: 1.5, justifyContent: 'space-between' }}>
          <Typography variant="caption" color="text.secondary">
            Press <strong>ESC</strong> or click Close to return to order
          </Typography>
          <Button
            variant="contained"
            onClick={() => setCostModal({ ...costModal, open: false })}
            sx={{ borderRadius: 1, px: 3 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      
      <Dialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>POS Keyboard Shortcuts</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle1" gutterBottom><b>F2</b> or <b>F9</b>: Create / Save Order</Typography>
          <Typography variant="subtitle1" gutterBottom><b>F3</b> or <b>F8</b>: Create &amp; Hold Order</Typography>
          <Typography variant="subtitle1" gutterBottom><b>F4</b> or <b>F10</b>: Save &amp; Print Receipt</Typography>
          <Typography variant="subtitle1" gutterBottom><b>F1</b>: Focus Product Search / Barcode Scanner</Typography>
          <Typography variant="subtitle1" gutterBottom><b>F5</b>: Focus Customer Search</Typography>
          <Typography variant="subtitle1" gutterBottom><b>F6</b>: Cancel Current Order</Typography>
          <Typography variant="subtitle1" gutterBottom><b>F7</b>: View Item Cost &amp; Profit Margin Analysis</Typography>
          <Typography variant="subtitle1" gutterBottom><b>F11</b> or <b>Alt+H</b>: View Sales Price History (select row first)</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShortcutsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Import from Quotation Dialog */}
      <Dialog
        open={importQuotationModalOpen}
        onClose={() => setImportQuotationModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 1.25 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ bgcolor: 'primary.light', color: 'primary.contrastText', p: 1, borderRadius: 1, display: 'flex' }}>
              <DescriptionIcon />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                Import Quotation into Sales Order
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Select an open quotation to pre-fill customer, products, prices, and discounts
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setImportQuotationModalOpen(false)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search quotation # or customer name..."
              value={quotationSearchTerm}
              onChange={(e) => setQuotationSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ borderRadius: 1 }}
            />
          </Box>
          {fetchingQuotationsList ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 360, border: '1px solid #E2E8F0', borderRadius: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Quotation #</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Customer</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Grand Total</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    const list = quotationsListData?.quotations || [];
                    const filtered = list.filter((q) => {
                      if (!quotationSearchTerm.trim()) return true;
                      const term = quotationSearchTerm.toLowerCase();
                      return (
                        q.quotationNumber?.toLowerCase().includes(term) ||
                        q.customer?.name?.toLowerCase().includes(term)
                      );
                    });

                    if (filtered.length === 0) {
                      return (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                            No matching quotations found.
                          </TableCell>
                        </TableRow>
                      );
                    }

                    return filtered.map((q) => (
                      <TableRow key={q.id} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{q.quotationNumber}</TableCell>
                        <TableCell>{q.customer?.name || 'Walk-in'}</TableCell>
                        <TableCell>{q.quotationDate ? new Date(q.quotationDate).toLocaleDateString() : '-'}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: 'success.main' }}>
                          {formatAmount(q.grandTotal)}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={q.status}
                            size="small"
                            color={q.status === 'converted' ? 'success' : q.status === 'sent' ? 'primary' : 'default'}
                            sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, textTransform: 'capitalize', borderRadius: 1 }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            variant="contained"
                            disabled={q.status === 'converted'}
                            onClick={() => {
                              setActiveQuotationId(q.id);
                              setSearchParams({ quotationId: q.id });
                              setImportQuotationModalOpen(false);
                            }}
                            sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.75rem', py: 0.25, px: 1.5, borderRadius: 1 }}
                          >
                            {q.status === 'converted' ? 'Already Converted' : 'Load into Order'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ));
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button onClick={() => setImportQuotationModalOpen(false)} sx={{ borderRadius: 1 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SalesOrderForm;

